import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox-shadcn';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/UI/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip-shadcn';
import PermissionHelpTooltip from '../components/permission_help_tooltip';
import { toast, type ExternalToast } from 'sonner';
import {
  ArrowRight,
  AtSign,
  Check,
  CircleCheck,
  CircleHelp,
  ClipboardList,
  CreditCard,
  ExternalLink,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  TriangleAlert,
  UserRound,
  X,
} from 'lucide-react';
import { FadeLoader } from 'react-spinners';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import WideSheet from '../components/wide_sheet/WideSheet';
import UserCheckIcon from '../assets/icons/user-check.svg';
import { addNewRole, deleteRole, editRole, getAllRolesByAccountId } from '../api/roles';
import {
  buyAdditionalSeats,
  changeSubscriptionPlan,
  getAccount,
  getSeatInfo,
  updateAccount,
  updateAdditionalSeats,
} from '../api/account';
import { getCustomerPlanInfo } from '../api/stripe';
import { getUser, handleUpdateUser, inviteMember } from '../api/users';
import {
  addNewAssignee,
  convertAssigneeToTeamMember,
  deleteAssignee,
  editAssignee,
  getAllAssigneesByAccountId,
} from '../api/assignees';
// import { Config } from '../utils';
import { checkGate } from '../statsig';
import type { AssigneePayload } from '../api/assignees';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';

export const PRICING_TABLE_PLANS_GATE = 'pricing_table_plans';

export const withCloseButton = (options?: ExternalToast): ExternalToast => ({
  ...options,
  closeButton: true,
});

export const showToast = (message: string, options?: ExternalToast): void => {
  toast(message, withCloseButton(options));
};

export const showSuccessToast = (message: string, options?: ExternalToast): void => {
  toast.success(message, withCloseButton(options));
};

export const showErrorToast = (message: string, options?: ExternalToast): void => {
  toast.error(message, withCloseButton(options));
};

const ownerPermissionsTooltipText = 'Owner permissions are always enabled';

export type TeamSettingsTab = 'team_members_permissions' | 'roles';
export type PermissionKey = 'projects' | 'items';
export type InvitePanelMode = 'team_member' | 'assignee';
export type PermissionTooltipKey = 'pricing' | 'admin';
export type TeamMemberPermissionKey = 'pricingEnabled' | 'isAdmin';

export interface RolePermissionRow {
  id: number;
  name: string;
  projects: boolean;
  items: boolean;
}

export interface TeamMemberRow {
  id: string;
  accountId: number;
  firstName: string;
  lastName: string;
  email: string;
  label: string;
  roles: number[];
  isOwner: boolean;
  isAdmin: boolean;
  pricingEnabled: boolean;
}

export interface TeamMemberPermissionDraft {
  accountId: number;
  pricingEnabled: boolean;
  isAdmin: boolean;
}

export interface AssigneeWithoutLoginRow {
  id: number;
  name: string;
  label: string;
  roles: number[];
}

interface ConvertAssigneeSheetState {
  id: number;
  name: string;
  label: string;
}

export interface PendingInvitationRow {
  id: string;
  accountId: number;
  pastAssigneeId?: number | null;
  email: string;
  label: string;
  roleIds: number[];
  sentLabel: string;
  statusLabel: string;
  isAdmin: boolean;
  canSeePricing: boolean;
}

type RolesEditorTarget = { type: 'team_member'; id: string } | { type: 'assignee'; id: number };

interface RolesEditorPosition {
  top: number;
  left: number;
}

interface ReadonlyRolesViewerState {
  key: string;
  roleNames: string[];
  top: number;
  left: number;
}

export interface TablePermissionTooltipPosition {
  top: number;
  left: number;
  width: number;
}

export const normalizeAssigneeLabelInput = (value: string): string =>
  value.replace(/^@+/, '').replace(/\s+/g, '').toUpperCase().slice(0, 4);

const deriveAssigneeLabelFromName = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '';
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return words
    .slice(0, 4)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
};

export const extractApiErrorMessage = (error: any): string => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string') {
    return message;
  }
  if (message && typeof message?.message === 'string') {
    return message.message;
  }
  return '';
};

const isLabelAlreadyInUseError = (message: string, statusCode?: number): boolean => {
  if (statusCode === 409) {
    return true;
  }
  const normalized = String(message || '').toLowerCase();
  return (
    normalized.includes('label is already in use for this account') ||
    (normalized.includes('label') && normalized.includes('already') && normalized.includes('use'))
  );
};

export const collectUsedLabels = (
  teamMembers: TeamMemberRow[],
  pendingInvitations: PendingInvitationRow[],
  assignees: AssigneeWithoutLoginRow[],
  excludeAssigneeId?: number
): Set<string> => {
  const usedLabels = new Set<string>();

  for (const member of teamMembers) {
    const normalized = normalizeAssigneeLabelInput(String(member?.label || ''));
    if (normalized) {
      usedLabels.add(normalized);
    }
  }
  for (const invitation of pendingInvitations) {
    const normalized = normalizeAssigneeLabelInput(String(invitation?.label || ''));
    if (normalized) {
      usedLabels.add(normalized);
    }
  }

  for (const assignee of assignees) {
    if (excludeAssigneeId && Number(assignee?.id) === Number(excludeAssigneeId)) {
      continue;
    }
    const normalized = normalizeAssigneeLabelInput(String(assignee?.label || ''));
    if (normalized) {
      usedLabels.add(normalized);
    }
  }

  return usedLabels;
};

export const generateUniqueAssigneeLabel = (name: string, usedLabels: Set<string>): string => {
  const baseLabel = normalizeAssigneeLabelInput(deriveAssigneeLabelFromName(name));
  if (!baseLabel) {
    return '';
  }

  if (!usedLabels.has(baseLabel)) {
    return baseLabel;
  }

  for (let suffix = 1; suffix <= 9999; suffix += 1) {
    const suffixText = String(suffix);
    const prefixLength = Math.max(1, 4 - suffixText.length);
    const candidate = `${baseLabel.slice(0, prefixLength)}${suffixText}`;
    if (!usedLabels.has(candidate)) {
      return candidate;
    }
  }

  return baseLabel;
};

interface TeamAccessCache {
  isAdmin: boolean;
  isOwner: boolean;
  teamScopeAccountId: number;
}

const getTeamAccessCacheKey = (accountId: number): string => `team-settings-access-${accountId}`;

const readTeamAccessCache = (accountId: number): TeamAccessCache | null => {
  if (!accountId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(getTeamAccessCacheKey(accountId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    const isAdmin = !!(parsed as any).isAdmin;
    const isOwner = !!(parsed as any).isOwner;
    const cachedScopeAccountId = Number((parsed as any).teamScopeAccountId);
    const teamScopeAccountId =
      Number.isFinite(cachedScopeAccountId) && cachedScopeAccountId > 0
        ? cachedScopeAccountId
        : accountId;

    return {
      isAdmin,
      isOwner,
      teamScopeAccountId,
    };
  } catch (error) {
    return null;
  }
};

const writeTeamAccessCache = (accountId: number, cache: TeamAccessCache) => {
  if (!accountId) {
    return;
  }

  try {
    localStorage.setItem(getTeamAccessCacheKey(accountId), JSON.stringify(cache));
  } catch (error) {
    // No-op: cache is best-effort only.
  }
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

export const readOwnerAccountIdFromStorage = (): number => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const ownerAccountId = toNumber(localStorage.getItem('ownerAccountId'));
  return ownerAccountId && ownerAccountId > 0 ? ownerAccountId : 0;
};

export const resolveOwnerAccountIdFromTeamContext = (
  teamContext: any,
  fallbackAccountId: number
): number => {
  const directOwnerId = toNumber(teamContext?.ownerAccountId);
  if (directOwnerId && directOwnerId > 0) {
    return directOwnerId;
  }

  const ownerObjectId = toNumber(teamContext?.owner?.accountId);
  if (ownerObjectId && ownerObjectId > 0) {
    return ownerObjectId;
  }

  const teamMembers = Array.isArray(teamContext?.teamMembers) ? teamContext.teamMembers : [];
  const ownerTeamMember = teamMembers.find((member: any) => !!member?.isOwner);
  const ownerTeamMemberId = toNumber(ownerTeamMember?.accountId);
  if (ownerTeamMemberId && ownerTeamMemberId > 0) {
    return ownerTeamMemberId;
  }

  return fallbackAccountId;
};

export const extractSeatLimitFromPlanInfo = (planInfo: any): number =>
  Math.max(toNumber(planInfo?.product_metadata?.seats) ?? 0, 0);

export const normalizeRoleIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((roleId) => Number(roleId))
    .filter((roleId) => Number.isInteger(roleId) && roleId > 0);
};

export const formatTimeAgo = (value: unknown): string => {
  if (!value) {
    return 'Unknown';
  }

  const timestamp = new Date(value as string).getTime();
  if (Number.isNaN(timestamp)) {
    return 'Unknown';
  }

  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) {
    return 'Just now';
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ago`;
  }
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)}h ago`;
  }
  return `${Math.floor(seconds / 86400)}d ago`;
};

export const mapAssigneePayloadToRow = (assignee: AssigneePayload): AssigneeWithoutLoginRow => ({
  id: Number(assignee.id),
  name: String(assignee.name ?? '').trim(),
  label: normalizeAssigneeLabelInput(String(assignee.label ?? '')),
  roles: normalizeRoleIds(assignee.roles),
});

const resolveInitialTeamSettingsTab = (): TeamSettingsTab => {
  if (typeof window === 'undefined') {
    return 'team_members_permissions';
  }
  const searchTab = new URLSearchParams(window.location.search || '').get('tab');
  if (searchTab === 'roles') {
    return 'roles';
  }
  const hash = window.location.hash || '';
  const hashQueryIndex = hash.indexOf('?');
  const tab =
    hashQueryIndex >= 0 ? new URLSearchParams(hash.slice(hashQueryIndex + 1)).get('tab') : null;
  return tab === 'roles' ? 'roles' : 'team_members_permissions';
};

let initialSeatState = {
  interval: 'month',
  quantity: '1',
  cost: '0.00',
};

const TeamSettingsPage = (props: any): JSX.Element => {
  const accountId = parseFloat(localStorage.getItem('accountId') || '0');
  const ownerAccountIdFromStorage = readOwnerAccountIdFromStorage();
  const [teamScopeAccountId, setTeamScopeAccountId] = useState(accountId);
  const firstName = (localStorage.getItem('firstName') || '').trim();
  const lastName = (localStorage.getItem('lastName') || '').trim();
  const [accountIsOwner, setAccountIsOwner] = useState(false);
  const [accountIsAdmin, setAccountIsAdmin] = useState(false);
  const [hasResolvedAccountAccess, setHasResolvedAccountAccess] = useState(false);
  const canManageTeamMembers = accountIsOwner || accountIsAdmin;
  const canViewRolesTab = canManageTeamMembers;
  const canViewPendingInvitations = accountIsOwner || accountIsAdmin;
  const canViewSeatUsage = canManageTeamMembers;
  const [activeTab, setActiveTab] = useState<TeamSettingsTab>(resolveInitialTeamSettingsTab);
  const [roleName, setRoleName] = useState('');
  const [roles, setRoles] = useState<RolePermissionRow[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberRow[]>([]);
  const [assigneesWithoutLogin, setAssigneesWithoutLogin] = useState<AssigneeWithoutLoginRow[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitationRow[]>([]);
  const [isInvitePanelOpen, setIsInvitePanelOpen] = useState(false);
  const [invitePanelMode, setInvitePanelMode] = useState<InvitePanelMode>('team_member');
  const [convertAssigneeTarget, setConvertAssigneeTarget] =
    useState<ConvertAssigneeSheetState | null>(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [convertMemberEmail, setConvertMemberEmail] = useState('');
  const [memberSelectedRoleIds, setMemberSelectedRoleIds] = useState<number[]>([]);
  const [memberPricingAccess, setMemberPricingAccess] = useState(false);
  const [memberAdminAccess, setMemberAdminAccess] = useState(false);
  const [convertMemberPricingAccess, setConvertMemberPricingAccess] = useState(false);
  const [convertMemberAdminAccess, setConvertMemberAdminAccess] = useState(false);
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [isConvertingAssignee, setIsConvertingAssignee] = useState(false);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);
  const [deletingInvitationId, setDeletingInvitationId] = useState<string | null>(null);
  const [isRemovingInactiveMembers, setIsRemovingInactiveMembers] = useState(false);
  const [openPermissionTooltip, setOpenPermissionTooltip] = useState<PermissionTooltipKey | null>(
    null
  );
  const [openTablePermissionTooltip, setOpenTablePermissionTooltip] =
    useState<PermissionTooltipKey | null>(null);
  const [tablePermissionTooltipPosition, setTablePermissionTooltipPosition] =
    useState<TablePermissionTooltipPosition | null>(null);
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeLabel, setAssigneeLabel] = useState('');
  const [isAssigneeLabelAuto, setIsAssigneeLabelAuto] = useState(true);
  const [assigneeSelectedRoleIds, setAssigneeSelectedRoleIds] = useState<number[]>([]);
  const [editingAssigneeNameId, setEditingAssigneeNameId] = useState<number | null>(null);
  const [editingAssigneeLabelId, setEditingAssigneeLabelId] = useState<number | null>(null);
  const [editingAssigneeName, setEditingAssigneeName] = useState('');
  const [editingAssigneeLabel, setEditingAssigneeLabel] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [isEditingOwnerLabel, setIsEditingOwnerLabel] = useState(false);
  const [editingOwnerLabel, setEditingOwnerLabel] = useState('');
  const [isSavingOwnerLabel, setIsSavingOwnerLabel] = useState(false);
  const [editingTeamMemberLabelId, setEditingTeamMemberLabelId] = useState<string | null>(null);
  const [editingTeamMemberLabel, setEditingTeamMemberLabel] = useState('');
  const [isSavingTeamMemberLabel, setIsSavingTeamMemberLabel] = useState(false);
  const [rolesEditorTarget, setRolesEditorTarget] = useState<RolesEditorTarget | null>(null);
  const [rolesEditorPosition, setRolesEditorPosition] = useState<RolesEditorPosition | null>(null);
  const [rolesEditorSelectedRoleIds, setRolesEditorSelectedRoleIds] = useState<number[]>([]);
  const [isSavingRolesEditor, setIsSavingRolesEditor] = useState(false);
  const [teamMemberPermissionBaseline, setTeamMemberPermissionBaseline] = useState<
    Record<string, TeamMemberPermissionDraft>
  >({});
  const [pendingTeamMemberPermissions, setPendingTeamMemberPermissions] = useState<
    Record<string, TeamMemberPermissionDraft>
  >({});
  const [isSavingTeamMemberPermissions, setIsSavingTeamMemberPermissions] = useState(false);
  const [isSavePermissionsConfirmOpen, setIsSavePermissionsConfirmOpen] = useState(false);
  const [deletingTeamMemberId, setDeletingTeamMemberId] = useState<string | null>(null);
  const [assigneeMutationState, setAssigneeMutationState] = useState<
    null | 'adding' | 'editing' | 'deleting'
  >(null);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editingRoleName, setEditingRoleName] = useState('');
  const [isSavingRoleName, setIsSavingRoleName] = useState(false);
  const [roleMutationState, setRoleMutationState] = useState<
    null | 'adding' | 'deleting' | 'editing'
  >(null);
  const [planName, setPlanName] = useState('');
  const [usedSlots, setUsedSlots] = useState(0);
  const [defaultSeats, setDefaultSeats] = useState(0);
  const [availableSlots, setAvailableSlots] = useState(0);
  const [pendingSlots, setPendingSlots] = useState(0);
  const [readonlyRolesViewer, setReadonlyRolesViewer] = useState<ReadonlyRolesViewerState | null>(
    null
  );
  const [openSeatsModal, setOpenSeatsModal] = useState(false);
  const [openUpdateSeatsModal, setOpenUpdateSeatsModal] = useState(false);
  const [seatState, setSeatState] = useState(initialSeatState);
  const [availableSeats, setAvailableSeats] = useState<any>(null);
  const [seatLoading, setSeatLoading] = useState<boolean>(false);
  const [additionalSeat, setAdditionalSeat] = useState<any>(null);

  const managedAccountId = teamScopeAccountId || accountId;
  const ownerTeamMemberAccountId = teamMembers.find((member) => member.isOwner)?.accountId || 0;
  const assigneeScopeAccountId =
    ownerTeamMemberAccountId || ownerAccountIdFromStorage || managedAccountId;

  const hasRoleName = roleName.trim().length > 0;
  const isRoleMutationLoading = roleMutationState !== null;
  const isAssigneeMutationLoading = assigneeMutationState !== null;
  const isAssigneeEditOrDeleteLoading =
    assigneeMutationState === 'editing' || assigneeMutationState === 'deleting';
  const canAddRole = hasRoleName && !isRoleMutationLoading;
  const normalizedAvailableSlots = Math.max(availableSlots, 0);
  const seatUsagePercent = normalizedAvailableSlots
    ? Math.min((Math.max(usedSlots, 0) / normalizedAvailableSlots) * 100, 100)
    : 0;
  const isConvertPanelOpen = !!convertAssigneeTarget;
  const remainingSeatsAfterInvite = Math.max(normalizedAvailableSlots - (usedSlots + 1), 0);
  const isSeatLimitReached = usedSlots >= normalizedAvailableSlots;
  const canSubmitConvertPanel =
    convertMemberEmail.trim().includes('@') && !isSeatLimitReached && !isConvertingAssignee;
  const canSubmitInvitePanel =
    invitePanelMode === 'team_member'
      ? memberEmail.trim().length > 0 && !isSeatLimitReached && !isInvitingMember
      : assigneeName.trim().length > 0 &&
        assigneeLabel.trim().length > 0 &&
        assigneeSelectedRoleIds.length > 0 &&
        !isAssigneeMutationLoading;
  const isRolesEditorBusy = isSavingRolesEditor || isAssigneeMutationLoading;
  const hasPendingTeamMemberPermissionChanges =
    Object.keys(pendingTeamMemberPermissions).length > 0;
  const assigneesTableColumns = canManageTeamMembers
    ? 'grid-cols-[minmax(220px,1.3fr)_minmax(150px,0.9fr)_minmax(220px,1.3fr)_minmax(250px,1.1fr)]'
    : 'grid-cols-[minmax(220px,1.3fr)_minmax(150px,0.9fr)_minmax(260px,1.6fr)]';
  const assigneesTableMinWidth = canManageTeamMembers ? 'min-w-[1040px]' : 'min-w-[760px]';
  const teamMembersTableColumns = canManageTeamMembers
    ? 'grid-cols-[minmax(260px,1.7fr)_minmax(120px,0.9fr)_minmax(140px,1fr)_100px_100px_96px]'
    : 'grid-cols-[minmax(260px,1.7fr)_minmax(120px,0.9fr)_minmax(220px,1.3fr)]';
  const teamMembersTableMinWidth = canManageTeamMembers ? 'min-w-[860px]' : 'min-w-[620px]';
  // const seatsPricingTableId = Config.pricingTableSeatsId;
  // const canShowSeatsPricingTable = !!seatsPricingTableId;

  useEffect(() => {
    fetchAccountAccess();
  }, []);

  useEffect(() => {
    if (!managedAccountId) {
      return;
    }
    fetchRoles();
    loadSeatUsage();
    fetchTeamData();
  }, [managedAccountId]);

  useEffect(() => {
    void fetchAssignees();
  }, [assigneeScopeAccountId]);

  useEffect(() => {
    if (!hasResolvedAccountAccess) {
      return;
    }
    if (!canViewRolesTab && activeTab === 'roles') {
      setActiveTab('team_members_permissions');
    }
  }, [activeTab, canViewRolesTab, hasResolvedAccountAccess]);

  useEffect(() => {
    if (canManageTeamMembers) {
      setReadonlyRolesViewer(null);
      return;
    }

    setIsInvitePanelOpen(false);
    setConvertAssigneeTarget(null);
    setOpenPermissionTooltip(null);
    setOpenTablePermissionTooltip(null);
    setTablePermissionTooltipPosition(null);
    setRolesEditorTarget(null);
    setRolesEditorPosition(null);
    setRolesEditorSelectedRoleIds([]);
    setPendingTeamMemberPermissions({});
    setIsEditingOwnerLabel(false);
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
    setEditingAssigneeNameId(null);
    setEditingAssigneeLabelId(null);
    setConvertMemberEmail('');
    setConvertMemberPricingAccess(false);
    setConvertMemberAdminAccess(false);
  }, [canManageTeamMembers]);

  useEffect(() => {
    if (!isInvitePanelOpen && !isConvertPanelOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isInvitePanelOpen, isConvertPanelOpen]);

  useEffect(() => {
    if (!isAssigneeLabelAuto) {
      return;
    }
    if (invitePanelMode !== 'assignee') {
      return;
    }
    const uniqueLabel = getUniqueAssigneeLabel(assigneeName);
    if (uniqueLabel !== assigneeLabel) {
      setAssigneeLabel(uniqueLabel);
    }
  }, [
    isAssigneeLabelAuto,
    invitePanelMode,
    assigneeName,
    assigneeLabel,
    teamMembers,
    assigneesWithoutLogin,
  ]);

  useEffect(() => {
    if (openPermissionTooltip === null) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        setOpenPermissionTooltip(null);
        return;
      }

      const clickedTrigger = target.closest('[data-permission-tooltip-trigger]');
      const clickedTooltipContent = target.closest('[data-permission-tooltip-content]');
      if (!clickedTrigger && !clickedTooltipContent) {
        setOpenPermissionTooltip(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openPermissionTooltip]);

  useEffect(() => {
    if (openTablePermissionTooltip === null) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        setOpenTablePermissionTooltip(null);
        setTablePermissionTooltipPosition(null);
        return;
      }

      const clickedTrigger = target.closest('[data-table-permission-tooltip-trigger]');
      const clickedTooltipContent = target.closest('[data-table-permission-tooltip-content]');
      if (!clickedTrigger && !clickedTooltipContent) {
        setOpenTablePermissionTooltip(null);
        setTablePermissionTooltipPosition(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openTablePermissionTooltip]);

  useEffect(() => {
    if (openTablePermissionTooltip === null) {
      return;
    }

    const closeTooltip = () => {
      setOpenTablePermissionTooltip(null);
      setTablePermissionTooltipPosition(null);
    };

    window.addEventListener('resize', closeTooltip);
    window.addEventListener('scroll', closeTooltip, true);

    return () => {
      window.removeEventListener('resize', closeTooltip);
      window.removeEventListener('scroll', closeTooltip, true);
    };
  }, [openTablePermissionTooltip]);

  useEffect(() => {
    if (rolesEditorTarget === null) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        setRolesEditorTarget(null);
        setRolesEditorPosition(null);
        setRolesEditorSelectedRoleIds([]);
        return;
      }

      const clickedRolesEditor = target.closest('[data-roles-editor]');
      if (!clickedRolesEditor) {
        setRolesEditorTarget(null);
        setRolesEditorPosition(null);
        setRolesEditorSelectedRoleIds([]);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [rolesEditorTarget]);

  useEffect(() => {
    if (readonlyRolesViewer === null) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        setReadonlyRolesViewer(null);
        return;
      }

      const clickedPopup = target.closest('[data-readonly-roles-viewer]');
      const clickedTrigger = target.closest('[data-readonly-roles-trigger]');
      if (!clickedPopup && !clickedTrigger) {
        setReadonlyRolesViewer(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [readonlyRolesViewer]);

  useEffect(() => {
    if (readonlyRolesViewer === null) {
      return;
    }

    const closeViewer = () => {
      setReadonlyRolesViewer(null);
    };

    window.addEventListener('resize', closeViewer);
    window.addEventListener('scroll', closeViewer, true);

    return () => {
      window.removeEventListener('resize', closeViewer);
      window.removeEventListener('scroll', closeViewer, true);
    };
  }, [readonlyRolesViewer]);

  const handleAddNewRole = async () => {
    const trimmedRoleName = roleName.trim();
    if (!trimmedRoleName) {
      showToast('Role name is required', {
        description: 'Please enter a valid role name before adding.',
      });
      return;
    }
    if (isRoleMutationLoading) {
      return;
    }

    try {
      setRoleMutationState('adding');
      await addNewRole({
        accountId: managedAccountId,
        name: trimmedRoleName,
        items: false,
        projects: false,
      });
      await fetchRoles();
      setRoleName('');
    } catch (error) {
      if ((error as any)?.response?.status === 409) {
        showErrorToast('Role already exists. Please use a different name.');
        return;
      }
      if ((error as any)?.response?.status === 403) {
        showErrorToast('You do not have permission to add roles.');
        return;
      }
      showErrorToast('Failed to add role. Please try again later.');
    } finally {
      setRoleMutationState(null);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const handleToggleRolePermission = async (id: number, key: PermissionKey, checked: boolean) => {
    if (isRoleMutationLoading) {
      return;
    }

    try {
      setRoleMutationState('editing');
      if (key === 'items') {
        await editRole({ accountId: managedAccountId, id, items: checked });
      } else {
        await editRole({ accountId: managedAccountId, id, projects: checked });
      }

      await fetchRoles();
    } catch (error) {
      if ((error as any)?.response?.status === 403) {
        showErrorToast('You do not have permission to edit this role.');
        return;
      }
      showErrorToast('Failed to update role permission. Please try again later.');
    } finally {
      setRoleMutationState(null);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const handleStartRoleNameEdit = (role: RolePermissionRow) => {
    setEditingRoleId(role.id);
    setEditingRoleName(role.name);
  };

  const handleCancelRoleNameEdit = () => {
    setEditingRoleId(null);
    setEditingRoleName('');
  };

  const handleSaveRoleName = async (roleId: number) => {
    if (isRoleMutationLoading) {
      return;
    }

    const trimmedRoleName = editingRoleName.trim();
    if (!trimmedRoleName) {
      showErrorToast('Role name cannot be empty.');
      return;
    }

    const role = roles.find((item) => item.id === roleId);
    if (role && role.name === trimmedRoleName) {
      handleCancelRoleNameEdit();
      return;
    }

    try {
      setRoleMutationState('editing');
      setIsSavingRoleName(true);
      await editRole({ accountId: managedAccountId, id: roleId, name: trimmedRoleName });
      await fetchRoles();
      handleCancelRoleNameEdit();
    } catch (error) {
      if ((error as any)?.response?.status === 409) {
        showErrorToast('Role already exists. Please use a different name.');
        return;
      }
      if ((error as any)?.response?.status === 403) {
        showErrorToast('You do not have permission to edit this role.');
        return;
      }
      showErrorToast('Failed to update role name. Please try again later.');
    } finally {
      setIsSavingRoleName(false);
      setRoleMutationState(null);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (isRoleMutationLoading) {
      return;
    }

    try {
      setRoleMutationState('deleting');
      await deleteRole({ accountId: managedAccountId, id: roleId });

      setRoles((prev) => prev.filter((role) => role.id !== roleId));
      if (editingRoleId === roleId) {
        handleCancelRoleNameEdit();
      }
    } catch (error) {
      showErrorToast('Failed to delete role. Please try again later.');
    } finally {
      setRoleMutationState(null);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const fetchRoles = async () => {
    if (!managedAccountId) {
      setRoles([]);
      return;
    }

    try {
      const roles = await getAllRolesByAccountId({ accountId: managedAccountId });

      setRoles(roles);
    } catch (error) {
      showErrorToast('Failed to fetch roles. Please try again later.');
    }
  };

  const fetchAccountAccess = async () => {
    setHasResolvedAccountAccess(false);
    if (!accountId) {
      setAccountIsAdmin(false);
      setAccountIsOwner(false);
      setTeamScopeAccountId(0);
      setHasResolvedAccountAccess(true);
      return;
    }

    const cachedAccess = readTeamAccessCache(accountId);
    if (cachedAccess) {
      const resolvedCachedScopeAccountId =
        ownerAccountIdFromStorage > 0 ? ownerAccountIdFromStorage : cachedAccess.teamScopeAccountId;
      setAccountIsAdmin(cachedAccess.isAdmin);
      setAccountIsOwner(cachedAccess.isOwner);
      setTeamScopeAccountId(resolvedCachedScopeAccountId);
      if (resolvedCachedScopeAccountId !== cachedAccess.teamScopeAccountId) {
        writeTeamAccessCache(accountId, {
          isAdmin: cachedAccess.isAdmin,
          isOwner: cachedAccess.isOwner,
          teamScopeAccountId: resolvedCachedScopeAccountId,
        });
      }
      setHasResolvedAccountAccess(true);
      return;
    }

    try {
      const account = await getAccount(accountId);
      const isAdmin = !!account?.isAdmin;
      const isOwner = !!account?.isOwner;
      const parentAccountId = toNumber(account?.parentAccountId);
      const resolvedTeamScopeAccountId = isOwner
        ? accountId
        : ownerAccountIdFromStorage && ownerAccountIdFromStorage > 0
          ? ownerAccountIdFromStorage
          : parentAccountId && parentAccountId > 0
            ? parentAccountId
            : accountId;
      setAccountIsAdmin(isAdmin);
      setAccountIsOwner(isOwner);
      setTeamScopeAccountId(resolvedTeamScopeAccountId);
      writeTeamAccessCache(accountId, {
        isAdmin,
        isOwner,
        teamScopeAccountId: resolvedTeamScopeAccountId,
      });
    } catch (error) {
      setAccountIsAdmin(false);
      setAccountIsOwner(false);
      setTeamScopeAccountId(accountId);
    } finally {
      setHasResolvedAccountAccess(true);
    }
  };

  const fetchAssignees = async (scopeAccountId: number = assigneeScopeAccountId) => {
    if (!scopeAccountId) {
      setAssigneesWithoutLogin([]);
      return;
    }

    try {
      const assignees = await getAllAssigneesByAccountId({ accountId: scopeAccountId });
      setAssigneesWithoutLogin((assignees || []).map(mapAssigneePayloadToRow));
    } catch (error) {
      showErrorToast('Failed to fetch assignees. Please try again later.');
    }
  };

  const loadSeatUsage = async () => {
    if (!managedAccountId) {
      return;
    }

    try {
      const planInfoRes = await getCustomerPlanInfo(managedAccountId);
      let planInfo = planInfoRes['main'];
      let planInfoSeat = planInfoRes['seat'];

      setAdditionalSeat(planInfoSeat || null);

      if (!planInfoRes || planInfoRes.error || !planInfo) {
        return;
      }
      if (typeof planInfo.planName === 'string') {
        setPlanName(planInfo.planName);
      }
      let mainPlanInfo = extractSeatLimitFromPlanInfo(planInfo);
      let seatsSum = (planInfoSeat?.quantity || 0) + mainPlanInfo;
      setAvailableSlots(seatsSum);
      setDefaultSeats(mainPlanInfo || 0)
    } catch (error) {
      // Seat usage is supplemental UI data; keep page functional if unavailable.
      console.error('Failed to load seat usage:', error);
    }
  };

  const fetchTeamData = async () => {
    if (!accountId) {
      return;
    }

    try {
      const user = await getUser({ accountId, includeTeamData: true });
      const ownerAccountId = resolveOwnerAccountIdFromTeamContext(
        user?.teamContext,
        ownerAccountIdFromStorage || managedAccountId || accountId
      );

      if (ownerAccountId && ownerAccountId !== managedAccountId) {
        setTeamScopeAccountId(ownerAccountId);
      }

      const allTeamMembers = Array.isArray(user?.teamContext?.teamMembers)
        ? user.teamContext.teamMembers
        : [];
      const members = allTeamMembers.filter(
        (member: any) => String(member?.type || '').toLowerCase() === 'subscribed'
      );

      const mappedMembers: TeamMemberRow[] = members.map((member: any) => ({
        id: !!member?.isOwner ? 'owner' : `account-${Number(member?.accountId || 0)}`,
        accountId: Number(member?.accountId || 0),
        firstName: String(member?.firstName || '').trim(),
        lastName: String(member?.lastName || '').trim(),
        email: String(member?.email || '').trim(),
        label: normalizeAssigneeLabelInput(String(member?.label || '').toUpperCase()),
        roles: normalizeRoleIds(member?.roles),
        isOwner: !!member?.isOwner,
        isAdmin: !!member?.isAdmin,
        pricingEnabled: !!member?.canSeePricing,
      }));

      let resolvedTeamMembers: TeamMemberRow[] = mappedMembers;
      if (mappedMembers.length === 0) {
        const fallbackIsOwner =
          !!user?.isOwner || (ownerAccountId > 0 && ownerAccountId === accountId);
        const fallbackIsAdmin = !!user?.isAdmin;
        resolvedTeamMembers = [
          {
            id: 'owner',
            accountId: ownerAccountId || accountId,
            firstName,
            lastName,
            email: String(user?.email || '').trim(),
            label: normalizeAssigneeLabelInput(String(user?.label || '').toUpperCase()),
            roles: normalizeRoleIds(user?.roles),
            isOwner: fallbackIsOwner,
            isAdmin: fallbackIsAdmin,
            pricingEnabled: !!user?.canSeePricing,
          },
        ];
      }
      setTeamMembers(resolvedTeamMembers);
      setTeamMemberPermissionBaseline(
        resolvedTeamMembers.reduce<Record<string, TeamMemberPermissionDraft>>((acc, member) => {
          acc[member.id] = {
            accountId: member.accountId,
            pricingEnabled: member.pricingEnabled,
            isAdmin: member.isAdmin,
          };
          return acc;
        }, {})
      );
      setPendingTeamMemberPermissions({});

      const ownerMember = mappedMembers.find((member) => member.isOwner);
      const ownerMemberSource = allTeamMembers.find(
        (member: any) => Number(member?.accountId) === ownerMember?.accountId
      );
      const ownerEmailFromContext =
        typeof user?.teamContext?.owner?.email === 'string' ? user.teamContext.owner.email : '';
      const currentUserEmail = typeof user?.email === 'string' ? user.email : '';
      setOwnerEmail(
        String(ownerMemberSource?.email || ownerEmailFromContext || currentUserEmail || '').trim()
      );

      const invitations = Array.isArray(user?.teamContext?.pendingInvitations)
        ? user.teamContext.pendingInvitations
        : [];
      const pendingInvitationsCount = invitations.length;
      setPendingSlots(pendingInvitationsCount);
      const ownerExistsInTeam = mappedMembers.some((member) => member.isOwner);
      const activeTeamMembersCount =
        mappedMembers.length > 0 ? mappedMembers.length + (ownerExistsInTeam ? 0 : 1) : 1;
      setUsedSlots(activeTeamMembersCount + pendingInvitationsCount);
      setPendingInvitations(
        invitations.map((invitation: any) => ({
          id: `pending-account-${Number(invitation?.accountId || 0)}`,
          accountId: Number(invitation?.accountId || 0),
          pastAssigneeId:
            Number.isInteger(Number(invitation?.pastAssigneeId)) &&
            Number(invitation?.pastAssigneeId) > 0
              ? Number(invitation.pastAssigneeId)
              : null,
          email: String(invitation?.email || '').trim(),
          label: normalizeAssigneeLabelInput(String(invitation?.label || '').toUpperCase()),
          roleIds: normalizeRoleIds(invitation?.roles),
          sentLabel: formatTimeAgo(invitation?.createdAt),
          statusLabel: 'Pending',
          isAdmin: !!invitation?.isAdmin,
          canSeePricing: !!invitation?.canSeePricing,
        }))
      );
    } catch (error) {
      console.error('Failed to load team settings data:', error);
    }
  };

  const handleChangePlan = async () => {
    try {
      const variant = checkGate(PRICING_TABLE_PLANS_GATE) ? 'new' : 'old';
      const session = await changeSubscriptionPlan(managedAccountId, variant);

      if (session?.url) {
        window.location.href = session.url;
        return;
      }

      const errorMessage = extractApiErrorMessage(session?.error) || session?.message;
      showErrorToast(errorMessage || 'Unable to open change plan page.');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      showErrorToast(errorMessage || 'Unable to open change plan page.');
    }
  };

  const handleChangeSeatQty = (e: ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    let numberRegex = /^[1-9]\d*$/;
    if (name === 'update_qty') {
      numberRegex = /^(0|[1-9]\d*)$/;
    }
    if (value && !numberRegex.test(value)) return;

    setSeatState((prev) => {
      return {
        ...prev,
        quantity: value,
      };
    });
  };

  const handleOpenUpdateSeatsModal = async (val: boolean) => {
    setOpenUpdateSeatsModal(val);
    setIsInvitePanelOpen(false);
    setSeatLoading(true);
    if (val) {
      const res = await getSeatInfo(accountId);
      if (res && !res?.error) {
        setAvailableSeats(res || null);
        setSeatLoading(false);
      }

      let unit_amount = additionalSeat?.plan?.amount_decimal || 0;
      let existingCost = unit_amount / 100 || 0;
      setSeatState((prev) => {
        return {
          ...prev,
          interval: additionalSeat?.plan?.interval || 'month',
          quantity: additionalSeat?.quantity?.toString() || '0',
          cost: existingCost?.toFixed(2) || '0.00',
        };
      });
    } else {
      setSeatState(initialSeatState);
    }
  };

  const handleOpenSeatsModal = async (val: boolean) => {
    setOpenSeatsModal(val);
    setIsInvitePanelOpen(false);
    setConvertAssigneeTarget(null);
    if (val) {
      setSeatLoading(true);
      const res = await getSeatInfo(accountId);
      if (res) {
        let defaultIntervalPrice: any = res?.prices?.data?.find(
          (i: any) => i?.recurring?.interval === seatState.interval
        );
        let unit_amount = defaultIntervalPrice?.unit_amount || 0;
        let defaultIntervalCost = unit_amount / 100 || 0;

        setAvailableSeats(res);
        setSeatState((prev) => {
          return {
            ...prev,
            cost: defaultIntervalCost?.toFixed(2),
          };
        });
        setSeatLoading(false);
      }
    } else {
      setAvailableSeats(null);
      setSeatState(initialSeatState);
      setSeatLoading(false);
    }
  };

  const handleChangeInterval = (val: string) => {
    setSeatState((prev) => ({ ...prev, interval: val }));
    let foundInterval = availableSeats?.prices?.data?.find(
      (i: any) => i?.recurring?.interval === val
    );
    let unit_amount = foundInterval?.unit_amount || 0;
    let newCost = unit_amount / 100 || 0;
    setSeatState((prev) => {
      return {
        ...prev,
        cost: newCost?.toFixed(2),
      };
    });
  };

  const handleUpdateExistingSeats = async () => {
    let nQty = +seatState.quantity || 0;
    let newTotalSeats = defaultSeats + nQty
    if (newTotalSeats < usedSlots) {
      showErrorToast('Unable to reduce the number of seats due to the number of team members');
      return;
    }

    setSeatLoading(true);
    let foundInterval = availableSeats?.prices?.data?.find(
      (i: any) => i?.recurring?.interval === seatState.interval
    );

    let payload = {
      accountId: accountId,
      quantity: Number(seatState.quantity) || 0,
      newPriceInterval: foundInterval.id,
      seat: additionalSeat,
    };
    const res = await updateAdditionalSeats(payload);
    if (res) {
      setOpenUpdateSeatsModal(false);
      setSeatState(initialSeatState);
      await Promise.all([fetchTeamData(), loadSeatUsage()]);
      setSeatLoading(false);
      showSuccessToast('Seats successfully updated.');
    } else if (!res || res.error) {
      showErrorToast('Error while updating seats.');
    }
  };

  const handleBuyMoreSeats = async () => {
    setSeatLoading(true);
    let priceObj = availableSeats?.prices?.data?.find(
      (i: any) => i?.recurring?.interval === seatState.interval
    );
    let payload = {
      accountId: accountId,
      quantity: Number(seatState.quantity) || 1,
      seat: priceObj,
    };
    const res = await buyAdditionalSeats(payload);
    if (res) {
      setOpenSeatsModal(false);
      setSeatState(initialSeatState);
      await Promise.all([fetchTeamData(), loadSeatUsage()]);
      setSeatLoading(false);
      showSuccessToast('Seats successfully added.');
    } else if (!res || res.error) {
      showErrorToast('Error while adding seats.');
    }
  };

  // const handleAddSeats = async (closeInvitePanel = false) => {
  //   if (closeInvitePanel) {
  //     setIsInvitePanelOpen(false);
  //   }

  //   const session = await buyExtraSeats(managedAccountId);
  //   if (session?.url) {
  //     window.location.href = session.url;
  //     return;
  //   }

  //   const errorMessage = extractApiErrorMessage(session?.error) || session?.message;
  //   showErrorToast(errorMessage || 'Unable to open seats billing portal.');
  // };

  const ensureCanManageTeamMembers = (): boolean => {
    if (canManageTeamMembers) {
      return true;
    }
    showErrorToast('You do not have permission to edit team members.');
    return false;
  };

  const getUsedAssigneeLabels = (excludeAssigneeId?: number): Set<string> =>
    collectUsedLabels(teamMembers, pendingInvitations, assigneesWithoutLogin, excludeAssigneeId);

  const getUniqueAssigneeLabel = (name: string): string =>
    generateUniqueAssigneeLabel(name, getUsedAssigneeLabels());

  const resetInvitePanelForm = (mode: InvitePanelMode) => {
    if (mode === 'team_member') {
      setMemberEmail('');
      setMemberSelectedRoleIds([]);
      setMemberPricingAccess(false);
      setMemberAdminAccess(false);
      return;
    }
    setAssigneeName('');
    setAssigneeLabel('');
    setIsAssigneeLabelAuto(true);
    setAssigneeSelectedRoleIds([]);
  };

  const resetConvertPanelForm = () => {
    setConvertMemberEmail('');
    setConvertMemberPricingAccess(false);
    setConvertMemberAdminAccess(false);
  };

  const handleOpenInvitePanel = (mode: InvitePanelMode) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setConvertAssigneeTarget(null);
    setInvitePanelMode(mode);
    resetInvitePanelForm(mode);
    setOpenTablePermissionTooltip(null);
    setTablePermissionTooltipPosition(null);
    setIsInvitePanelOpen(true);

    if (mode === 'assignee') {
      void Promise.all([fetchTeamData(), fetchAssignees()]);
    }
  };

  const handleCloseInvitePanel = () => {
    setIsInvitePanelOpen(false);
    setOpenPermissionTooltip(null);
  };

  const handleOpenConvertAssigneePanel = (assignee: AssigneeWithoutLoginRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setIsInvitePanelOpen(false);
    setConvertAssigneeTarget({
      id: assignee.id,
      name: assignee.name,
      label: assignee.label,
    });
    resetConvertPanelForm();
    setOpenPermissionTooltip(null);
    setOpenTablePermissionTooltip(null);
    setTablePermissionTooltipPosition(null);
    void loadSeatUsage();
  };

  const handleCloseConvertAssigneePanel = () => {
    setConvertAssigneeTarget(null);
    resetConvertPanelForm();
    setOpenPermissionTooltip(null);
  };

  const handleSubmitConvertAssigneePanel = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (!canSubmitConvertPanel) {
      return;
    }

    const trimmedEmail = convertMemberEmail.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      showErrorToast('Please enter a valid email.');
      return;
    }
    if (!accountId) {
      showErrorToast('Account not found.');
      return;
    }
    if (!convertAssigneeTarget) {
      showErrorToast('Assignee not found.');
      return;
    }

    try {
      setIsConvertingAssignee(true);
      await convertAssigneeToTeamMember({
        accountId,
        assigneeId: convertAssigneeTarget.id,
        email: trimmedEmail,
        canSeePricing: convertMemberPricingAccess,
        isAdmin: convertMemberAdminAccess,
      });
      await Promise.all([fetchTeamData(), fetchAssignees(), loadSeatUsage()]);
      showSuccessToast(
        `Conversion started - An invitation has been sent to ${trimmedEmail}. ${convertAssigneeTarget.name} will be removed from assignees.`
      );
      handleCloseConvertAssigneePanel();
    } catch (error: any) {
      const apiMessage = extractApiErrorMessage(error);
      showErrorToast(apiMessage || 'Failed to convert assignee. Please try again later.');
    } finally {
      setIsConvertingAssignee(false);
    }
  };

  const handleSwitchInviteMode = (mode: InvitePanelMode) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setConvertAssigneeTarget(null);
    setInvitePanelMode(mode);
    resetInvitePanelForm(mode);
    setOpenPermissionTooltip(null);

    if (mode === 'assignee') {
      void Promise.all([fetchTeamData(), fetchAssignees()]);
    }
  };

  const getTablePermissionTooltipPosition = (
    triggerElement: HTMLButtonElement,
    key: PermissionTooltipKey
  ): TablePermissionTooltipPosition => {
    const width = key === 'pricing' ? 290 : 320;
    const tooltipHeight = key === 'pricing' ? 86 : 110;
    const offset = 8;
    const rect = triggerElement.getBoundingClientRect();
    const preferredLeft = rect.left + rect.width / 2 - width / 2;
    const maxLeft = Math.max(offset, window.innerWidth - width - offset);
    const left = Math.min(Math.max(preferredLeft, offset), maxLeft);

    const availableBelow = window.innerHeight - rect.bottom - offset;
    const top =
      availableBelow >= tooltipHeight
        ? rect.bottom + offset
        : Math.max(offset, rect.top - tooltipHeight - offset);

    return { top, left, width };
  };

  const handleToggleTablePermissionTooltip = (
    key: PermissionTooltipKey,
    triggerElement: HTMLButtonElement
  ) => {
    setOpenTablePermissionTooltip((prev) => {
      if (prev === key) {
        setTablePermissionTooltipPosition(null);
        return null;
      }
      setTablePermissionTooltipPosition(getTablePermissionTooltipPosition(triggerElement, key));
      return key;
    });
  };

  const handleUpdateTeamMemberPermission = (
    member: TeamMemberRow,
    key: TeamMemberPermissionKey,
    checked: boolean
  ) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isSavingTeamMemberPermissions || deletingTeamMemberId) {
      return;
    }
    if (member.isOwner) {
      return;
    }
    if (!member.accountId) {
      showErrorToast('Account not found.');
      return;
    }

    const currentValue = key === 'pricingEnabled' ? member.pricingEnabled : member.isAdmin;
    if (currentValue === checked) {
      return;
    }

    const nextPricingEnabled = key === 'pricingEnabled' ? checked : member.pricingEnabled;
    const nextIsAdmin = key === 'isAdmin' ? checked : member.isAdmin;

    setTeamMembers((prev) =>
      prev.map((teamMember) =>
        teamMember.id === member.id
          ? {
              ...teamMember,
              pricingEnabled: nextPricingEnabled,
              isAdmin: nextIsAdmin,
            }
          : teamMember
      )
    );

    const baseline = teamMemberPermissionBaseline[member.id] || {
      accountId: member.accountId,
      pricingEnabled: member.pricingEnabled,
      isAdmin: member.isAdmin,
    };

    setPendingTeamMemberPermissions((prev) => {
      const next = { ...prev };
      const unchanged =
        baseline.pricingEnabled === nextPricingEnabled && baseline.isAdmin === nextIsAdmin;

      if (unchanged) {
        delete next[member.id];
      } else {
        next[member.id] = {
          accountId: member.accountId,
          pricingEnabled: nextPricingEnabled,
          isAdmin: nextIsAdmin,
        };
      }

      return next;
    });
  };

  const handleSaveTeamMemberPermissions = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isSavingTeamMemberPermissions || deletingTeamMemberId) {
      return;
    }

    const pendingEntries = Object.entries(pendingTeamMemberPermissions);
    if (pendingEntries.length === 0) {
      return;
    }

    const successfulUpdates: Array<[string, TeamMemberPermissionDraft]> = [];
    const failedMemberIds: string[] = [];

    try {
      setIsSavingTeamMemberPermissions(true);
      await Promise.all(
        pendingEntries.map(async ([memberId, draft]) => {
          try {
            await updateAccount({
              accountId: draft.accountId,
              canSeePricing: draft.pricingEnabled,
              isAdmin: draft.isAdmin,
            });
            successfulUpdates.push([memberId, draft]);
          } catch (error) {
            failedMemberIds.push(memberId);
          }
        })
      );

      if (successfulUpdates.length > 0) {
        setTeamMemberPermissionBaseline((prev) => {
          const next = { ...prev };
          successfulUpdates.forEach(([memberId, draft]) => {
            next[memberId] = { ...draft };
          });
          return next;
        });

        const currentUserUpdate = successfulUpdates.find(
          ([, draft]) => Number(draft.accountId) === accountId
        );
        if (currentUserUpdate) {
          const [, draft] = currentUserUpdate;
          setAccountIsAdmin(draft.isAdmin);
          writeTeamAccessCache(accountId, {
            isAdmin: draft.isAdmin,
            isOwner: accountIsOwner,
            teamScopeAccountId,
          });
        }
      }

      if (failedMemberIds.length > 0) {
        setTeamMembers((prev) =>
          prev.map((teamMember) => {
            if (!failedMemberIds.includes(teamMember.id)) {
              return teamMember;
            }
            const baseline = teamMemberPermissionBaseline[teamMember.id];
            if (!baseline) {
              return teamMember;
            }
            return {
              ...teamMember,
              pricingEnabled: baseline.pricingEnabled,
              isAdmin: baseline.isAdmin,
            };
          })
        );

        setPendingTeamMemberPermissions((prev) => {
          const next = { ...prev };
          successfulUpdates.forEach(([memberId]) => {
            delete next[memberId];
          });
          return next;
        });

        showErrorToast('Failed to update some permissions. Please review and try again.');
        return;
      }

      setPendingTeamMemberPermissions({});
      showSuccessToast('Permissions updated.');
    } catch (error) {
      showErrorToast('Failed to update permissions. Please try again later.');
    } finally {
      setIsSavingTeamMemberPermissions(false);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const handleOpenSaveTeamMemberPermissionsConfirm = () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (!hasPendingTeamMemberPermissionChanges) {
      return;
    }
    if (isSavingTeamMemberPermissions || deletingTeamMemberId) {
      return;
    }
    setIsSavePermissionsConfirmOpen(true);
  };

  const handleConfirmSaveTeamMemberPermissions = () => {
    setIsSavePermissionsConfirmOpen(false);
    void handleSaveTeamMemberPermissions();
  };

  const handleDeleteTeamMember = async (member: TeamMemberRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (member.isOwner) {
      return;
    }
    if (isSavingTeamMemberPermissions || deletingTeamMemberId) {
      return;
    }
    if (!member.accountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setDeletingTeamMemberId(member.id);
      await updateAccount({
        accountId: member.accountId,
        type: 'unsubscribed',
      });
      if (rolesEditorTarget?.type === 'team_member' && rolesEditorTarget.id === member.id) {
        handleCloseRolesEditor();
      }
      if (editingTeamMemberLabelId === member.id) {
        handleCancelTeamMemberLabelEdit();
      }
      await Promise.all([fetchTeamData(), loadSeatUsage()]);
      const memberName =
        [member.firstName, member.lastName].filter(Boolean).join(' ') ||
        member.email ||
        'Team member';
      showSuccessToast(`${memberName} removed from team`);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        showErrorToast('You do not have permission to remove team members.');
      } else if (error?.response?.status === 404) {
        showErrorToast('Team member not found.');
      } else {
        showErrorToast('Failed to remove team member. Please try again later.');
      }
    } finally {
      setDeletingTeamMemberId(null);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const toggleMemberRole = (roleId: number, checked: boolean) => {
    setMemberSelectedRoleIds((prev) => {
      if (checked) {
        if (prev.includes(roleId)) {
          return prev;
        }
        return [...prev, roleId];
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const toggleAssigneeRole = (roleId: number, checked: boolean) => {
    setAssigneeSelectedRoleIds((prev) => {
      if (checked) {
        if (prev.includes(roleId)) {
          return prev;
        }
        return [...prev, roleId];
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const handleAssigneeNameChange = (value: string) => {
    setAssigneeName(value);
    if (isAssigneeLabelAuto) {
      setAssigneeLabel(getUniqueAssigneeLabel(value));
    }
  };

  const handleAssigneeLabelChange = (value: string) => {
    const normalizedLabel = normalizeAssigneeLabelInput(value);
    setAssigneeLabel(normalizedLabel);
    setIsAssigneeLabelAuto(false);
  };

  const handleStartAssigneeNameEdit = (assignee: AssigneeWithoutLoginRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setRolesEditorTarget(null);
    setRolesEditorPosition(null);
    setRolesEditorSelectedRoleIds([]);
    setIsEditingOwnerLabel(false);
    setEditingOwnerLabel('');
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
    setEditingAssigneeNameId(assignee.id);
    setEditingAssigneeLabelId(null);
    setEditingAssigneeName(assignee.name);
    setEditingAssigneeLabel('');
  };

  const handleStartAssigneeLabelEdit = (assignee: AssigneeWithoutLoginRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setRolesEditorTarget(null);
    setRolesEditorPosition(null);
    setRolesEditorSelectedRoleIds([]);
    setIsEditingOwnerLabel(false);
    setEditingOwnerLabel('');
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
    setEditingAssigneeLabelId(assignee.id);
    setEditingAssigneeNameId(null);
    setEditingAssigneeName('');
    setEditingAssigneeLabel(assignee.label);
  };

  const handleCancelAssigneeNameEdit = () => {
    setEditingAssigneeNameId(null);
    setEditingAssigneeName('');
  };

  const handleCancelAssigneeLabelEdit = () => {
    setEditingAssigneeLabelId(null);
    setEditingAssigneeLabel('');
  };

  const handleSaveAssigneeNameEdit = async (assigneeId: number) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isAssigneeMutationLoading) {
      return;
    }

    const trimmedName = editingAssigneeName.trim();
    if (!trimmedName) {
      showErrorToast('Assignee name is required.');
      return;
    }

    const assignee = assigneesWithoutLogin.find((item) => item.id === assigneeId);
    if (!assignee) {
      showErrorToast('Assignee not found.');
      return;
    }

    if (assignee.name === trimmedName) {
      handleCancelAssigneeNameEdit();
      return;
    }
    if (!assigneeScopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setAssigneeMutationState('editing');
      await editAssignee({ accountId: assigneeScopeAccountId, id: assigneeId, name: trimmedName });
      await fetchAssignees();
      handleCancelAssigneeNameEdit();
    } catch (error) {
      showErrorToast('Failed to update assignee. Please try again later.');
    } finally {
      setAssigneeMutationState(null);
    }
  };

  const handleSaveAssigneeLabelEdit = async (assigneeId: number) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isAssigneeMutationLoading) {
      return;
    }

    const trimmedLabel = normalizeAssigneeLabelInput(editingAssigneeLabel);
    if (!trimmedLabel || trimmedLabel.length < 1 || trimmedLabel.length > 4) {
      showErrorToast('Label must be 1 to 4 characters.');
      return;
    }

    const assignee = assigneesWithoutLogin.find((item) => item.id === assigneeId);
    if (!assignee) {
      showErrorToast('Assignee not found.');
      return;
    }

    if (assignee.label === trimmedLabel) {
      handleCancelAssigneeLabelEdit();
      return;
    }
    if (!assigneeScopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setAssigneeMutationState('editing');
      await editAssignee({
        accountId: assigneeScopeAccountId,
        id: assigneeId,
        label: trimmedLabel,
      });
      await fetchAssignees();
      handleCancelAssigneeLabelEdit();
    } catch (error: any) {
      const apiMessage = extractApiErrorMessage(error);
      showErrorToast(apiMessage || 'Failed to update assignee. Please try again later.');
    } finally {
      setAssigneeMutationState(null);
    }
  };

  const handleStartOwnerLabelEdit = (currentLabel: string) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setRolesEditorTarget(null);
    setRolesEditorPosition(null);
    setRolesEditorSelectedRoleIds([]);
    setEditingAssigneeNameId(null);
    setEditingAssigneeLabelId(null);
    setEditingAssigneeName('');
    setEditingAssigneeLabel('');
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
    setIsEditingOwnerLabel(true);
    setEditingOwnerLabel(currentLabel);
  };

  const handleCancelOwnerLabelEdit = () => {
    setIsEditingOwnerLabel(false);
    setEditingOwnerLabel('');
  };

  const handleStartTeamMemberLabelEdit = (member: TeamMemberRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (member.isOwner) {
      handleStartOwnerLabelEdit(member.label);
      return;
    }

    setRolesEditorTarget(null);
    setRolesEditorPosition(null);
    setRolesEditorSelectedRoleIds([]);
    setEditingAssigneeNameId(null);
    setEditingAssigneeLabelId(null);
    setEditingAssigneeName('');
    setEditingAssigneeLabel('');
    setIsEditingOwnerLabel(false);
    setEditingOwnerLabel('');
    setEditingTeamMemberLabelId(member.id);
    setEditingTeamMemberLabel(member.label);
  };

  const handleCancelTeamMemberLabelEdit = () => {
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
  };

  const handleSaveOwnerLabelEdit = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isSavingOwnerLabel) {
      return;
    }

    const trimmedLabel = normalizeAssigneeLabelInput(editingOwnerLabel);
    if (!trimmedLabel || trimmedLabel.length < 1 || trimmedLabel.length > 4) {
      showErrorToast('Label must be 1 to 4 characters.');
      return;
    }

    const currentOwner = teamMembers.find((member) => member.isOwner);
    if (!currentOwner) {
      showErrorToast('Owner row not found.');
      return;
    }

    if (currentOwner.label === trimmedLabel) {
      handleCancelOwnerLabelEdit();
      return;
    }

    const fallbackEmail = (() => {
      try {
        const raw = localStorage.getItem('quipa-store');
        const parsed = raw ? JSON.parse(raw) : null;
        return typeof parsed?.user?.email === 'string' ? parsed.user.email : '';
      } catch (error) {
        return '';
      }
    })();
    const email = ownerEmail || fallbackEmail;

    if (!email) {
      showErrorToast('Unable to update owner label. Please refresh and try again.');
      return;
    }

    try {
      setIsSavingOwnerLabel(true);
      const response = await handleUpdateUser({
        user: { email },
        email,
        label: trimmedLabel,
      });

      if (response?.error) {
        showErrorToast('Failed to update owner label. Please try again later.');
        return;
      }

      setTeamMembers((prev) =>
        prev.map((member) => (member.isOwner ? { ...member, label: trimmedLabel } : member))
      );
      handleCancelOwnerLabelEdit();
    } catch (error) {
      showErrorToast('Failed to update owner label. Please try again later.');
    } finally {
      setIsSavingOwnerLabel(false);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const handleSaveTeamMemberLabelEdit = async (member: TeamMemberRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isSavingTeamMemberLabel) {
      return;
    }

    const trimmedLabel = normalizeAssigneeLabelInput(editingTeamMemberLabel);
    if (!trimmedLabel || trimmedLabel.length < 1 || trimmedLabel.length > 4) {
      showErrorToast('Label must be 1 to 4 characters.');
      return;
    }

    if (member.label === trimmedLabel) {
      handleCancelTeamMemberLabelEdit();
      return;
    }

    const usedLabels = getUsedAssigneeLabels();
    const currentMemberLabel = normalizeAssigneeLabelInput(member.label);
    if (currentMemberLabel) {
      usedLabels.delete(currentMemberLabel);
    }
    if (usedLabels.has(trimmedLabel)) {
      showErrorToast('Label is already in use for this account');
      return;
    }

    if (!member.email) {
      showErrorToast('Unable to update member label. Please refresh and try again.');
      return;
    }

    try {
      setIsSavingTeamMemberLabel(true);
      const response = await handleUpdateUser({
        user: { email: member.email },
        email: member.email,
        label: trimmedLabel,
      });

      if (response?.error) {
        const apiMessage =
          extractApiErrorMessage(response) ||
          extractApiErrorMessage(response?.error) ||
          (typeof response?.message === 'string' ? response.message : '');
        const responseStatusCode = Number(response?.statusCode);
        if (isLabelAlreadyInUseError(apiMessage, responseStatusCode)) {
          showErrorToast('Label is already in use for this account');
          return;
        }
        showErrorToast(apiMessage || 'Failed to update member label. Please try again later.');
        return;
      }

      setTeamMembers((prev) =>
        prev.map((teamMember) =>
          teamMember.id === member.id ? { ...teamMember, label: trimmedLabel } : teamMember
        )
      );
      handleCancelTeamMemberLabelEdit();
    } catch (error: any) {
      const apiMessage = extractApiErrorMessage(error);
      const errorStatusCode = Number(error?.response?.status);
      if (isLabelAlreadyInUseError(apiMessage, errorStatusCode)) {
        showErrorToast('Label is already in use for this account');
        return;
      }
      showErrorToast(apiMessage || 'Failed to update member label. Please try again later.');
    } finally {
      setIsSavingTeamMemberLabel(false);
      // Refetch user
      if (props.handleRefetchUser) {
        props.handleRefetchUser();
      }
    }
  };

  const getRoleLabel = (selectedRoleIds: number[]) => {
    const selectedRoleNames = roles
      .filter((role) => selectedRoleIds.includes(role.id))
      .map((role) => role.name);
    return selectedRoleNames.length > 0 ? selectedRoleNames.join(', ') : 'No roles';
  };

  const getSelectedRoleNames = (selectedRoleIds: number[]) =>
    roles.filter((role) => selectedRoleIds.includes(role.id)).map((role) => role.name);

  const handleOpenReadonlyRolesViewer = (
    key: string,
    roleNames: string[],
    triggerElement: HTMLButtonElement
  ) => {
    if (roleNames.length === 0) {
      return;
    }

    setReadonlyRolesViewer((prev) => {
      if (prev?.key === key) {
        return null;
      }

      const position = getRolesEditorPosition(triggerElement, roleNames.length);
      return {
        key,
        roleNames: [...roleNames],
        top: position.top,
        left: position.left,
      };
    });
  };

  const areRoleSetsEqual = (first: number[], second: number[]) => {
    if (first.length !== second.length) {
      return false;
    }
    const firstSorted = [...first].sort((a, b) => a - b);
    const secondSorted = [...second].sort((a, b) => a - b);
    return firstSorted.every((roleId, index) => roleId === secondSorted[index]);
  };

  const getRolesEditorPosition = (
    triggerElement: HTMLButtonElement,
    rowsCountOverride?: number
  ): RolesEditorPosition => {
    const popupWidth = 230;
    const rowHeight = 36;
    const rowGap = 8;
    const resolvedRowsCount = Number.isFinite(rowsCountOverride)
      ? Number(rowsCountOverride)
      : roles.length;
    const rowsCount = Math.max(resolvedRowsCount, 1);
    const listHeight = rowsCount * rowHeight + (rowsCount - 1) * rowGap;
    const popupHeight = listHeight + 24;
    const offset = 8;
    const rect = triggerElement.getBoundingClientRect();
    const preferredLeft = rect.left + rect.width / 2 - popupWidth / 2;
    const maxLeft = Math.max(offset, window.innerWidth - popupWidth - offset);
    const left = Math.min(Math.max(preferredLeft, offset), maxLeft);

    const availableBelow = window.innerHeight - rect.bottom - offset;
    const availableAbove = rect.top - offset;
    const shouldOpenBelow = availableBelow >= popupHeight || availableBelow >= availableAbove;

    const unclampedTop = shouldOpenBelow ? rect.bottom + offset : rect.top - popupHeight - offset;
    const maxTop = Math.max(offset, window.innerHeight - popupHeight - offset);
    const top = Math.min(Math.max(unclampedTop, offset), maxTop);

    return { top, left };
  };

  const handleCloseRolesEditor = () => {
    setRolesEditorTarget(null);
    setRolesEditorPosition(null);
    setRolesEditorSelectedRoleIds([]);
    setIsSavingRolesEditor(false);
  };

  const handleOpenMemberRolesEditor = (
    member: TeamMemberRow,
    triggerElement: HTMLButtonElement
  ) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isRolesEditorBusy) {
      return;
    }
    if (roles.length === 0) {
      showErrorToast('Add roles first to assign them.');
      return;
    }

    setEditingAssigneeNameId(null);
    setEditingAssigneeLabelId(null);
    setIsEditingOwnerLabel(false);
    setEditingOwnerLabel('');
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
    setRolesEditorTarget({ type: 'team_member', id: member.id });
    setRolesEditorPosition(getRolesEditorPosition(triggerElement));
    setRolesEditorSelectedRoleIds([...member.roles]);
  };

  const handleOpenAssigneeRolesEditor = (
    assignee: AssigneeWithoutLoginRow,
    triggerElement: HTMLButtonElement
  ) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isRolesEditorBusy) {
      return;
    }
    if (roles.length === 0) {
      showErrorToast('Add roles first to assign them.');
      return;
    }

    setEditingAssigneeNameId(null);
    setEditingAssigneeLabelId(null);
    setIsEditingOwnerLabel(false);
    setEditingOwnerLabel('');
    setEditingTeamMemberLabelId(null);
    setEditingTeamMemberLabel('');
    setRolesEditorTarget({ type: 'assignee', id: assignee.id });
    setRolesEditorPosition(getRolesEditorPosition(triggerElement));
    setRolesEditorSelectedRoleIds([...assignee.roles]);
  };

  const handleRolesEditorRoleChange = async (roleId: number, checked: boolean) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (!rolesEditorTarget || isRolesEditorBusy) {
      return;
    }

    const nextRoleIds = checked
      ? rolesEditorSelectedRoleIds.includes(roleId)
        ? rolesEditorSelectedRoleIds
        : [...rolesEditorSelectedRoleIds, roleId]
      : rolesEditorSelectedRoleIds.filter((id) => id !== roleId);

    setRolesEditorSelectedRoleIds(nextRoleIds);

    if (rolesEditorTarget.type === 'team_member') {
      const member = teamMembers.find((item) => item.id === rolesEditorTarget.id);
      if (!member) {
        showErrorToast('Team member not found.');
        return;
      }
      if (areRoleSetsEqual(member.roles, nextRoleIds)) {
        return;
      }

      if (!member.accountId) {
        showErrorToast('Account not found.');
        setRolesEditorSelectedRoleIds([...member.roles]);
        return;
      }

      try {
        setIsSavingRolesEditor(true);
        await updateAccount({
          accountId: member.accountId,
          roles: [...nextRoleIds],
        });
        setTeamMembers((prev) =>
          prev.map((item) =>
            item.id === rolesEditorTarget.id ? { ...item, roles: [...nextRoleIds] } : item
          )
        );
      } catch (error) {
        showErrorToast('Failed to update account roles. Please try again later.');
        setRolesEditorSelectedRoleIds([...member.roles]);
      } finally {
        setIsSavingRolesEditor(false);
        // Refetch user
        if (props.handleRefetchUser) {
          props.handleRefetchUser();
        }
      }
      return;
    }

    const assignee = assigneesWithoutLogin.find((item) => item.id === rolesEditorTarget.id);
    if (!assignee) {
      showErrorToast('Assignee not found.');
      return;
    }
    if (areRoleSetsEqual(assignee.roles, nextRoleIds)) {
      return;
    }
    if (!assigneeScopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setIsSavingRolesEditor(true);
      await editAssignee({
        accountId: assigneeScopeAccountId,
        id: assignee.id,
        roles: [...nextRoleIds],
      });
      setAssigneesWithoutLogin((prev) =>
        prev.map((item) => (item.id === assignee.id ? { ...item, roles: [...nextRoleIds] } : item))
      );
    } catch (error) {
      showErrorToast('Failed to update assignee roles. Please try again later.');
      setRolesEditorSelectedRoleIds([...assignee.roles]);
    } finally {
      setIsSavingRolesEditor(false);
    }
  };

  const handleSubmitInvitePanel = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (invitePanelMode === 'team_member') {
      if (isSeatLimitReached) {
        showErrorToast('Seat limit reached. Add seats before inviting more team members.');
        return;
      }
      if (isInvitingMember) {
        return;
      }
      const trimmedEmail = memberEmail.trim();
      if (!trimmedEmail || !trimmedEmail.includes('@')) {
        showErrorToast('Please enter a valid email.');
        return;
      }
      if (!accountId) {
        showErrorToast('Account not found.');
        return;
      }

      try {
        setIsInvitingMember(true);
        await inviteMember({
          accountId,
          email: trimmedEmail,
          roles: memberSelectedRoleIds,
          canSeePricing: memberPricingAccess,
          isAdmin: memberAdminAccess,
        });
        await Promise.all([fetchTeamData(), loadSeatUsage()]);
        showSuccessToast(`Invitation sent - An invitation has been sent to ${trimmedEmail}`);
        handleCloseInvitePanel();
      } catch (error: any) {
        if (error?.response?.status === 409) {
          showErrorToast('A user with this email address is already a team member.');
        } else if (error?.response?.status === 403) {
          showErrorToast('You do not have permission to invite team members.');
        } else {
          showErrorToast('Failed to send invitation. Please try again later.');
        }
      } finally {
        setIsInvitingMember(false);
      }
      return;
    }

    const trimmedName = assigneeName.trim();
    const trimmedLabel = normalizeAssigneeLabelInput(assigneeLabel);
    if (!trimmedName) {
      showErrorToast('Assignee name is required.');
      return;
    }
    if (!trimmedLabel || trimmedLabel.length < 1 || trimmedLabel.length > 4) {
      showErrorToast('Label must be 1 to 4 characters.');
      return;
    }
    if (getUsedAssigneeLabels().has(trimmedLabel)) {
      showErrorToast('Label is already in use for this account');
      return;
    }
    if (assigneeSelectedRoleIds.length === 0) {
      showErrorToast('Select at least one role for the assignee.');
      return;
    }

    if (!assigneeScopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setAssigneeMutationState('adding');
      await addNewAssignee({
        accountId: assigneeScopeAccountId,
        name: trimmedName,
        label: trimmedLabel,
        roles: assigneeSelectedRoleIds,
      });
      await fetchAssignees();
      showSuccessToast(`Assignee added - ${trimmedName} has been added as assignee`);
      handleCloseInvitePanel();
    } catch (error: any) {
      const apiMessage = extractApiErrorMessage(error);
      showErrorToast(apiMessage || 'Failed to add assignee. Please try again later.');
    } finally {
      setAssigneeMutationState(null);
    }
  };

  const handleDeleteAssignee = async (assigneeId: number) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isAssigneeMutationLoading) {
      return;
    }
    if (!assigneeScopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setAssigneeMutationState('deleting');
      await deleteAssignee({ accountId: assigneeScopeAccountId, id: assigneeId });
      await fetchAssignees();
      if (editingAssigneeNameId === assigneeId) {
        handleCancelAssigneeNameEdit();
      }
      if (editingAssigneeLabelId === assigneeId) {
        handleCancelAssigneeLabelEdit();
      }
      if (rolesEditorTarget?.type === 'assignee' && rolesEditorTarget.id === assigneeId) {
        handleCloseRolesEditor();
      }
    } catch (error) {
      showErrorToast('Failed to delete assignee. Please try again later.');
    } finally {
      setAssigneeMutationState(null);
    }
  };

  const handleResendInvitation = async (invitation: PendingInvitationRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (resendingInvitationId || deletingInvitationId) {
      return;
    }
    if (!accountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setResendingInvitationId(invitation.id);
      await inviteMember({
        accountId,
        email: invitation.email,
        roles: invitation.roleIds,
        canSeePricing: invitation.canSeePricing,
        isAdmin: invitation.isAdmin,
      });
      await Promise.all([fetchTeamData(), loadSeatUsage()]);
      showSuccessToast(`Invitation resent to ${invitation.email}`);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        showErrorToast('You do not have permission to resend invitations.');
      } else if (error?.response?.status === 404) {
        showErrorToast('Pending invitation not found.');
      } else {
        showErrorToast('Failed to resend invitation. Please try again later.');
      }
    } finally {
      setResendingInvitationId(null);
    }
  };

  const removePendingInvitation = async (invitation: PendingInvitationRow) => {
    await updateAccount({
      accountId: invitation.accountId,
      type: 'unsubscribed',
    });

    if (invitation.pastAssigneeId && assigneeScopeAccountId) {
      await editAssignee({
        accountId: assigneeScopeAccountId,
        id: invitation.pastAssigneeId,
        label: invitation.label,
        isDeleted: false,
        isConverted: false,
      });
    }
  };

  const handleDeleteInvitation = async (invitation: PendingInvitationRow) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (resendingInvitationId || deletingInvitationId) {
      return;
    }

    if (!invitation?.accountId) {
      showErrorToast('Invitation account not found.');
      return;
    }

    try {
      setDeletingInvitationId(invitation.id);
      await removePendingInvitation(invitation);
      await Promise.all([fetchTeamData(), loadSeatUsage()]);
      showSuccessToast(`Invitation removed for ${invitation.email}`);
    } catch (error) {
      showErrorToast('Failed to remove invitation. Please try again later.');
    } finally {
      setDeletingInvitationId(null);
    }
  };

  const isInvitationActionDisabled = (): boolean => {
    return !!deletingInvitationId || !!resendingInvitationId || isRemovingInactiveMembers;
  };

  const handleRemoveInactiveMembers = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isRemovingInactiveMembers || resendingInvitationId || deletingInvitationId) {
      return;
    }
    if (pendingInvitations.length === 0) {
      showToast('There is no inactive members');
      return;
    }

    try {
      setIsRemovingInactiveMembers(true);
      const results = await Promise.allSettled(
        pendingInvitations.map((invitation) => removePendingInvitation(invitation))
      );

      const failedCount = results.filter((result) => result.status === 'rejected').length;
      const successCount = results.length - failedCount;

      await Promise.all([fetchTeamData(), loadSeatUsage()]);

      if (failedCount > 0) {
        showErrorToast('Failed to remove some inactive members. Please try again later.');
        return;
      }

      const message =
        successCount === 1
          ? '1 inactive member removed.'
          : `${successCount} inactive members removed.`;
      showSuccessToast(message);
    } catch (error) {
      showErrorToast('Failed to remove inactive members. Please try again later.');
    } finally {
      setIsRemovingInactiveMembers(false);
    }
  };

  return (
    <div className="m-5 flex flex-1 flex-col">
      {isRoleMutationLoading ? (
        <div
          className="team-settings-loader-overlay"
          data-qa={`team-settings-${roleMutationState}-loader`}
        >
          <div className="team-settings-loader-spinner">
            <FadeLoader
              color="#3B97B1"
              aria-label={`${
                roleMutationState === 'adding'
                  ? 'Adding'
                  : roleMutationState === 'deleting'
                    ? 'Deleting'
                    : 'Editing'
              } role`}
              data-testid="team-settings-loader"
            />
          </div>
        </div>
      ) : null}
      {!isRoleMutationLoading && isAssigneeEditOrDeleteLoading ? (
        <div
          className="team-settings-loader-overlay"
          data-qa={`team-settings-assignee-${assigneeMutationState}-loader`}
        >
          <div className="team-settings-loader-spinner">
            <FadeLoader
              color="#3B97B1"
              aria-label={`${assigneeMutationState === 'deleting' ? 'Deleting' : 'Editing'} assignee`}
              data-testid="team-settings-assignee-loader"
            />
          </div>
        </div>
      ) : null}
      <div className="mb-[4px] px-4">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Team Settings</h1>
      </div>

      <div className="flex-1 px-4">
        {canManageTeamMembers ? (
          <>
            <p className="text-sm text-muted-foreground mb-6">Create roles and manage:</p>
            <ul className="text-sm text-muted-foreground mb-6 space-y-1 ml-4">
              <li>• Team members</li>
              <li>• Assignees who don't need logins</li>
            </ul>
          </>
        ) : null}

        {canViewRolesTab ? (
          <div
            className="filters_container client_filters"
            style={{ marginTop: '0px', marginBottom: '32px' }}
          >
            <div className="other_tables client-table-tabs" style={{ border: 'none' }}>
              <div>
                <button
                  className={`link_button ${activeTab === 'team_members_permissions' ? 'active' : ''} text-[14px]`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'team_members_permissions'}
                  onClick={() => setActiveTab('team_members_permissions')}
                  data-qa="team-settings-tab-team-members-permissions"
                >
                  Team Members & Permissions
                </button>
              </div>

              <div>
                <button
                  className={`link_button ${activeTab === 'roles' ? 'active' : ''} text-[14px]`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'roles'}
                  onClick={() => setActiveTab('roles')}
                  data-qa="team-settings-tab-roles"
                >
                  Roles
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'team_members_permissions' || !canViewRolesTab ? (
          <>
            {canViewSeatUsage ? (
              <div className="mt-4 w-full max-w-[1200px] rounded-[8px] bg-[#f1f5f980] px-4 py-5">
                <div className="flex items-center gap-3 leading-normal">
                  <CreditCard className="h-5 w-5 text-[#64748B]" />
                  <h3 className="m-0 text-[16px] leading-none font-semibold text-[#111827]">
                    Seat Usage
                  </h3>
                  {planName ? (
                    <span className="inline-flex items-center rounded-full border border-input px-2.5 py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground text-xs">
                      {planName}
                    </span>
                  ) : null}
                </div>

                <p className="mb-0 mt-5 text-[14px] font-semibold text-[#111827]">
                  {usedSlots} of {availableSlots} seats used
                  {pendingSlots > 0 ? (
                    <>
                      {' '}
                      <span className="font-normal text-[#60778F]">
                        (includes {pendingSlots} pending)
                      </span>
                    </>
                  ) : null}
                </p>

                <div className="mt-3 h-[8px] w-full max-w-[320px] rounded-full bg-[#D7E2E9]">
                  <div
                    className="h-full rounded-full bg-[#3B97B1]"
                    style={{ width: `${seatUsagePercent}%` }}
                  />
                </div>

                <p className="text-xs text-muted-foreground mt-2">
                  {planName || 'This plan'} includes up to {availableSlots} team members.{' '}
                  {accountIsOwner && (
                    <>
                      <Dialog
                        open={openSeatsModal}
                        onOpenChange={(val) => handleOpenSeatsModal(val)}
                      >
                        <DialogTrigger asChild>
                          <button
                            disabled={!accountIsOwner}
                            type="button"
                            className="underline inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                            onClick={() => handleOpenSeatsModal(true)}
                            data-qa="team_settings_add_seats_button"
                          >
                            Add Seats
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[700px]">
                          <DialogHeader>
                            <DialogTitle className="m-0 text-[24px]">Buy Extra Seats</DialogTitle>
                          </DialogHeader>

                          <div className="flex flex-col gap-4">
                            <div className="md:text-start text-center md:ml-4">
                              <RadioGroup
                                disabled={seatLoading}
                                className="flex items-center gap-1 rounded-md w-max"
                                value={seatState.interval}
                                onValueChange={(val) => handleChangeInterval(val)}
                              >
                                <div className="">
                                  <RadioGroupItem
                                    value={'month'}
                                    id="seat_monthly"
                                    className="hidden"
                                  />
                                  <Label
                                    data-loading={seatLoading}
                                    data-checked={seatState.interval === 'month'}
                                    htmlFor="seat_monthly"
                                    className="transition duration-300 cursor-pointer px-[12px] py-[8px] rounded-md data-[checked=true]:shadow-[0_0_0_2px_rgba(59,151,177,1)] border border-input data-[checked=false]:bg-white data-[loading=true]:opacity-50"
                                  >
                                    Monthly
                                  </Label>
                                </div>
                                <div className="">
                                  <RadioGroupItem
                                    value={'year'}
                                    id="seat_yearly"
                                    className="hidden"
                                  />
                                  <Label
                                    data-loading={seatLoading}
                                    data-checked={seatState.interval === 'year'}
                                    htmlFor="seat_yearly"
                                    className="transition duration-300 cursor-pointer px-[12px] py-[8px] rounded-md data-[checked=true]:shadow-[0_0_0_2px_rgba(59,151,177,1)] border border-input data-[checked=false]:bg-white data-[loading=true]:opacity-50"
                                  >
                                    Yearly
                                  </Label>
                                </div>
                              </RadioGroup>
                            </div>

                            <div className="p-[24px] rounded-[12px] border border-2 border-primary_dark">
                              <div className="flex md:flex-row flex-col justify-between gap-2">
                                <div>
                                  <div className="mb-4 flex items-center gap-2">
                                    <div>
                                      <Input
                                        disabled={seatLoading}
                                        onChange={(e) => handleChangeSeatQty(e)}
                                        className="px-1 text-center max-w-[60px] min-h-[10px] max-h-max"
                                        value={seatState.quantity}
                                        autoFocus
                                      />
                                    </div>
                                    <span className="text-[18px] font-medium">
                                      Extra Seat{Number(seatState.quantity) > 1 ? 's' : ''}
                                    </span>
                                  </div>

                                  <div>
                                    <span>
                                      $
                                      {(
                                        (+seatState.cost || 0) * (+seatState.quantity || 0)
                                      ).toFixed(2)}{' '}
                                      per {seatState.interval}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <div className="flex items-center gap-2 px-[16px] py-[8px] border border-input rounded-md">
                                    <CircleCheck className="w-[16px] h-[16px] text-primary" />
                                    <span>Selected</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <DialogFooter>
                            <DialogClose>
                              <Button type="button" variant={'outline'}>
                                Close
                              </Button>
                            </DialogClose>
                            <Button
                              disabled={seatLoading || !seatState.quantity}
                              onClick={() => handleBuyMoreSeats()}
                              type="button"
                            >
                              Buy
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>{' '}
                      or{' '}
                      <button
                        type="button"
                        className="underline inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                        onClick={() => {
                          void handleChangePlan();
                        }}
                        data-qa="team-settings-seat-usage-change-plan"
                      >
                        Change Plan
                      </button>{' '}
                      to add more seats.
                    </>
                  )}
                </p>

                {additionalSeat && accountIsOwner && (
                  <p className="text-xs text-muted-foreground">
                    Click{' '}
                    <Dialog
                      open={openUpdateSeatsModal}
                      onOpenChange={(val) => handleOpenUpdateSeatsModal(val)}
                    >
                      <DialogTrigger asChild>
                        <button type="button" className="underline text-primary">
                          here
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-[700px]">
                        <DialogHeader>
                          <DialogTitle className="m-0 text-[24px]">Update Extra Seats</DialogTitle>
                        </DialogHeader>

                        <div className="flex flex-col gap-4">
                          <div className="md:text-start text-center md:ml-4">
                            <RadioGroup
                              disabled={seatLoading}
                              className="flex items-center gap-1 rounded-md w-max"
                              value={seatState.interval}
                              onValueChange={(val) => handleChangeInterval(val)}
                            >
                              <div className="">
                                <RadioGroupItem
                                  value={'month'}
                                  id="seat_monthly"
                                  className="hidden"
                                />
                                <Label
                                  data-loading={seatLoading}
                                  data-checked={seatState.interval === 'month'}
                                  htmlFor="seat_monthly"
                                  className="transition duration-300 cursor-pointer px-[12px] py-[8px] rounded-md data-[checked=true]:shadow-[0_0_0_2px_rgba(59,151,177,1)] border border-input data-[checked=false]:bg-white data-[loading=true]:opacity-50"
                                >
                                  Monthly
                                </Label>
                              </div>
                              <div className="">
                                <RadioGroupItem
                                  value={'year'}
                                  id="seat_yearly"
                                  className="hidden"
                                />
                                <Label
                                  data-loading={seatLoading}
                                  data-checked={seatState.interval === 'year'}
                                  htmlFor="seat_yearly"
                                  className="transition duration-300 cursor-pointer px-[12px] py-[8px] rounded-md data-[checked=true]:shadow-[0_0_0_2px_rgba(59,151,177,1)] border border-input data-[checked=false]:bg-white data-[loading=true]:opacity-50"
                                >
                                  Yearly
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>

                          <div className="p-[24px] rounded-[12px] border border-2 border-primary_dark">
                            <div className="flex md:flex-row flex-col justify-between gap-2">
                              <div>
                                <div className="mb-4 flex items-center gap-2">
                                  <div>
                                    <Input
                                      name="update_qty"
                                      disabled={seatLoading}
                                      onChange={(e) => handleChangeSeatQty(e)}
                                      className="px-1 text-center max-w-[60px] min-h-[10px] max-h-max"
                                      value={seatState.quantity}
                                      autoFocus
                                    />
                                  </div>
                                  <span className="text-[18px] font-medium">
                                    Extra Seat{Number(seatState.quantity) > 1 ? 's' : ''}
                                  </span>
                                </div>

                                <div>
                                  <span>
                                    $
                                    {((+seatState.cost || 0) * (+seatState.quantity || 0)).toFixed(
                                      2
                                    )}{' '}
                                    per {seatState.interval}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center">
                                <div className="flex items-center gap-2 px-[16px] py-[8px] border border-input rounded-md">
                                  <CircleCheck className="w-[16px] h-[16px] text-primary" />
                                  <span>Selected</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <DialogFooter>
                          <DialogClose>
                            <Button type="button" variant={'outline'}>
                              Close
                            </Button>
                          </DialogClose>
                          <Button
                            disabled={seatLoading || !seatState.quantity}
                            onClick={() => handleUpdateExistingSeats()}
                            type="button"
                          >
                            Update
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>{' '}
                    to update additional seats.
                  </p>
                )}
              </div>
            ) : null}

            <div className="mt-5 w-full max-w-[1200px] rounded-[8px] bg-[#f1f5f980] px-4 py-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <UserCheckIcon className="h-5 w-5 text-[#64748B]" />
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Team Members</h3>
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#E3EAF1] px-2 text-[12px] font-semibold text-[#334155]">
                    {teamMembers.length}
                  </span>
                </div>
                {canManageTeamMembers ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[14px] font-medium text-[#2F8FB0] hover:text-[#267990]"
                    onClick={() => handleOpenInvitePanel('team_member')}
                    data-qa="team-settings-team-members-add-member"
                  >
                    <Plus className="h-4 w-4" />
                    Add Member
                  </button>
                ) : null}
              </div>

              <p className="mb-0 mt-4 text-[14px] text-[#60778F]">
                People with Quipa accounts who can log in, see projects based on their permissions,
                and receive notifications and @mentions.
              </p>

              <div className="mt-5 w-full overflow-x-auto rounded-[8px] border border-[#D8E0E8]">
                <div className={teamMembersTableMinWidth}>
                  <div
                    className={`grid ${teamMembersTableColumns} items-end border-b border-[#D8E0E8] px-4 py-4 text-[14px] text-[#546B8B]`}
                  >
                    <div className="font-normal">Name</div>
                    <div className="font-normal">Label</div>
                    <div className="font-normal">Roles</div>
                    {canManageTeamMembers ? (
                      <div className="flex items-center justify-center gap-1 font-normal">
                        Pricing
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 items-center justify-center rounded text-[#64748B] hover:bg-[#E2E8F0]"
                          onClick={(event) =>
                            handleToggleTablePermissionTooltip('pricing', event.currentTarget)
                          }
                          aria-label="Show pricing permission help"
                          data-table-permission-tooltip-trigger="pricing"
                          data-qa="team-settings-table-pricing-help"
                        >
                          <CircleHelp className="h-4 w-4" />
                        </button>
                      </div>
                    ) : null}
                    {canManageTeamMembers ? (
                      <div className="flex items-center justify-center gap-1 font-normal">
                        Admin
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 items-center justify-center rounded text-[#64748B] hover:bg-[#E2E8F0]"
                          onClick={(event) =>
                            handleToggleTablePermissionTooltip('admin', event.currentTarget)
                          }
                          aria-label="Show admin permission help"
                          data-table-permission-tooltip-trigger="admin"
                          data-qa="team-settings-table-admin-help"
                        >
                          <CircleHelp className="h-4 w-4" />
                        </button>
                      </div>
                    ) : null}
                    {canManageTeamMembers ? (
                      <div className="font-normal" aria-hidden="true" />
                    ) : null}
                  </div>

                  {teamMembers.map((member, index) => {
                    const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ');
                    const memberRoleNames = getSelectedRoleNames(member.roles);
                    const memberReadonlyRolesKey = `team-member-${member.id}`;
                    const isTeamMemberPermissionDisabled =
                      isSavingTeamMemberPermissions || deletingTeamMemberId !== null;
                    const isDeletingTeamMember = deletingTeamMemberId === member.id;
                    const isOwnerPermissionLocked = member.isOwner;
                    const pricingChecked = isOwnerPermissionLocked ? true : member.pricingEnabled;
                    const adminChecked = isOwnerPermissionLocked ? true : member.isAdmin;

                    return (
                      <div
                        key={member.id}
                        className={`grid ${teamMembersTableColumns} items-center px-4 py-4 ${
                          index < teamMembers.length - 1 ? 'border-b border-[#D8E0E8]' : ''
                        }`}
                        data-qa={`team-settings-team-member-row-${member.id}`}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-[14px] font-semibold text-[#0F172A]">
                            {fullName}
                          </span>
                          {member.isOwner ? (
                            <span className="inline-flex items-center rounded-full border border-input font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-[10px] px-1.5 py-0 h-4 text-purple-700 bg-purple-50 border-purple-200">
                              Owner
                            </span>
                          ) : null}
                          {member.isAdmin ? (
                            <span className="inline-flex items-center rounded-full border border-input font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-[10px] px-1.5 py-0 h-4 text-blue-700 bg-blue-50 border-blue-200">
                              Admin
                            </span>
                          ) : null}
                        </div>
                        <div className="min-w-0">
                          {member.isOwner ? (
                            canManageTeamMembers ? (
                              isEditingOwnerLabel ? (
                                <div className="inline-flex items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-2 py-2 text-[14px] text-[#0F172A]">
                                  <Input
                                    value={editingOwnerLabel}
                                    onChange={(e) =>
                                      setEditingOwnerLabel(
                                        normalizeAssigneeLabelInput(e.target.value)
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveOwnerLabelEdit();
                                      }
                                      if (e.key === 'Escape') {
                                        handleCancelOwnerLabelEdit();
                                      }
                                    }}
                                    className="h-8 w-[88px] border-[#9BB6D0] text-[14px] font-semibold uppercase"
                                    maxLength={4}
                                    disabled={isSavingOwnerLabel}
                                    autoFocus
                                    data-qa="team-settings-owner-label-input"
                                  />
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[#2F7A8E] hover:bg-[#D6E8EE] disabled:opacity-60"
                                    onClick={handleSaveOwnerLabelEdit}
                                    disabled={isSavingOwnerLabel}
                                    aria-label="Save owner label"
                                    data-qa="team-settings-owner-label-save"
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] disabled:opacity-60"
                                    onClick={handleCancelOwnerLabelEdit}
                                    disabled={isSavingOwnerLabel}
                                    aria-label="Cancel owner label edit"
                                    data-qa="team-settings-owner-label-cancel"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="inline-flex max-w-full items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-[8px] py-[4px] text-[14px] text-[#0F172A] hover:bg-[#F5F8FB] disabled:cursor-not-allowed"
                                  onClick={() => handleStartOwnerLabelEdit(member.label)}
                                  disabled={isSavingOwnerLabel}
                                  aria-label="Edit owner label"
                                  data-qa="team-settings-owner-label-edit"
                                >
                                  <span className="max-w-[80px] truncate">@{member.label}</span>
                                  <Pencil className="h-[12px] w-[12px] text-[#3B97B1]" />
                                </button>
                              )
                            ) : (
                              <div className="text-[14px] text-[#546B8B]">@{member.label}</div>
                            )
                          ) : canManageTeamMembers ? (
                            editingTeamMemberLabelId === member.id ? (
                              <div className="inline-flex items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-2 py-2 text-[14px] text-[#0F172A]">
                                <Input
                                  value={editingTeamMemberLabel}
                                  onChange={(e) =>
                                    setEditingTeamMemberLabel(
                                      normalizeAssigneeLabelInput(e.target.value)
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveTeamMemberLabelEdit(member);
                                    }
                                    if (e.key === 'Escape') {
                                      handleCancelTeamMemberLabelEdit();
                                    }
                                  }}
                                  className="h-8 w-[88px] border-[#9BB6D0] text-[14px] font-semibold uppercase"
                                  maxLength={4}
                                  disabled={isSavingTeamMemberLabel}
                                  autoFocus
                                  data-qa={`team-settings-team-member-${member.id}-label-input`}
                                />
                                <button
                                  type="button"
                                  className="inline-flex h-7 w-7 items-center justify-center rounded text-[#2F7A8E] hover:bg-[#D6E8EE] disabled:opacity-60"
                                  onClick={() => handleSaveTeamMemberLabelEdit(member)}
                                  disabled={isSavingTeamMemberLabel}
                                  aria-label={`Save label for ${fullName}`}
                                  data-qa={`team-settings-team-member-${member.id}-label-save`}
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex h-7 w-7 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] disabled:opacity-60"
                                  onClick={handleCancelTeamMemberLabelEdit}
                                  disabled={isSavingTeamMemberLabel}
                                  aria-label={`Cancel label edit for ${fullName}`}
                                  data-qa={`team-settings-team-member-${member.id}-label-cancel`}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex max-w-full items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-[8px] py-[4px] text-[14px] text-[#0F172A] hover:bg-[#F5F8FB] disabled:cursor-not-allowed"
                                onClick={() => handleStartTeamMemberLabelEdit(member)}
                                disabled={isSavingTeamMemberLabel}
                                aria-label={`Edit label for ${fullName}`}
                                data-qa={`team-settings-team-member-${member.id}-label-edit`}
                              >
                                <span className="max-w-[80px] truncate">@{member.label}</span>
                                <Pencil className="h-[12px] w-[12px] text-[#3B97B1]" />
                              </button>
                            )
                          ) : (
                            <div className="text-[14px] text-[#546B8B]">@{member.label}</div>
                          )}
                        </div>
                        <div className="relative" data-roles-editor>
                          {canManageTeamMembers ? (
                            <button
                              type="button"
                              className="inline-flex max-w-full items-center gap-2 rounded-[8px] border border-dashed border-transparent px-2 py-1 text-left hover:border-[#C2CDDC] hover:bg-[#f1f5f980] disabled:opacity-60"
                              onClick={(event) =>
                                handleOpenMemberRolesEditor(member, event.currentTarget)
                              }
                              disabled={isRolesEditorBusy}
                              data-qa={`team-settings-team-member-${member.id}-roles-edit`}
                            >
                              {memberRoleNames.length === 0 ? (
                                <span className="text-[14px] text-[#60778F]">No roles</span>
                              ) : (
                                <>
                                  {memberRoleNames.slice(0, 2).map((roleName, roleIndex) => (
                                    <span
                                      key={`${member.id}-role-pill-${roleName}-${roleIndex}`}
                                      className="inline-flex items-center rounded-full bg-[#F3F7FB] px-2 py-[2px] text-[12px] font-semibold text-[#3F5874]"
                                    >
                                      {roleName}
                                    </span>
                                  ))}
                                  {memberRoleNames.length > 2 ? (
                                    <span className="text-[12px] font-semibold text-[#3F5874]">
                                      +{memberRoleNames.length - 2} more
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </button>
                          ) : (
                            <div className="inline-flex max-w-full items-center gap-2 px-2 py-1 text-left">
                              {memberRoleNames.length === 0 ? (
                                <span className="text-[14px] text-[#60778F]">No roles</span>
                              ) : (
                                <button
                                  type="button"
                                  className="inline-flex max-w-full items-center gap-2 border-0 bg-transparent p-0 text-left"
                                  onClick={(event) =>
                                    handleOpenReadonlyRolesViewer(
                                      memberReadonlyRolesKey,
                                      memberRoleNames,
                                      event.currentTarget
                                    )
                                  }
                                  data-readonly-roles-trigger
                                  data-qa={`team-settings-team-member-${member.id}-roles-view`}
                                >
                                  {memberRoleNames.slice(0, 2).map((roleName, roleIndex) => (
                                    <span
                                      key={`${member.id}-readonly-role-pill-${roleName}-${roleIndex}`}
                                      className="inline-flex items-center rounded-full bg-[#F3F7FB] px-2 py-[2px] text-[12px] font-semibold text-[#3F5874]"
                                    >
                                      {roleName}
                                    </span>
                                  ))}
                                  {memberRoleNames.length > 2 ? (
                                    <span
                                      className="text-[12px] font-semibold text-[#3F5874]"
                                      data-qa={`team-settings-team-member-${member.id}-roles-more`}
                                    >
                                      +{memberRoleNames.length - 2} more
                                    </span>
                                  ) : null}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {canManageTeamMembers ? (
                          <div className="flex items-center justify-center">
                            {isOwnerPermissionLocked ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex cursor-not-allowed">
                                    <Checkbox
                                      checked={pricingChecked}
                                      disabled
                                      data-qa={`team-settings-team-member-${member.id}-pricing-checkbox`}
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  sideOffset={8}
                                  className="rounded-[8px] border border-[#D8E0E8] bg-white px-3 py-2 text-[14px] leading-[1.3] text-[#111827] shadow-md"
                                  arrowClassName="fill-white"
                                  data-qa={`team-settings-team-member-${member.id}-pricing-locked-tooltip`}
                                >
                                  {ownerPermissionsTooltipText}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Checkbox
                                checked={pricingChecked}
                                onCheckedChange={(checked) => {
                                  void handleUpdateTeamMemberPermission(
                                    member,
                                    'pricingEnabled',
                                    checked === true
                                  );
                                }}
                                disabled={isTeamMemberPermissionDisabled}
                                data-qa={`team-settings-team-member-${member.id}-pricing-checkbox`}
                              />
                            )}
                          </div>
                        ) : null}
                        {canManageTeamMembers ? (
                          <div className="flex items-center justify-center">
                            {isOwnerPermissionLocked ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex cursor-not-allowed">
                                    <Checkbox
                                      checked={adminChecked}
                                      disabled
                                      data-qa={`team-settings-team-member-${member.id}-admin-checkbox`}
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  sideOffset={8}
                                  className="rounded-[8px] border border-[#D8E0E8] bg-white px-3 py-2 text-[14px] leading-[1.3] text-[#111827] shadow-md"
                                  arrowClassName="fill-white"
                                  data-qa={`team-settings-team-member-${member.id}-admin-locked-tooltip`}
                                >
                                  {ownerPermissionsTooltipText}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <Checkbox
                                checked={adminChecked}
                                onCheckedChange={(checked) => {
                                  void handleUpdateTeamMemberPermission(
                                    member,
                                    'isAdmin',
                                    checked === true
                                  );
                                }}
                                disabled={isTeamMemberPermissionDisabled}
                                data-qa={`team-settings-team-member-${member.id}-admin-checkbox`}
                              />
                            )}
                          </div>
                        ) : null}
                        {canManageTeamMembers ? (
                          <div className="flex items-center justify-center">
                            {member.isOwner ? null : (
                              <Button
                                variant={'trash-button'}
                                type="button"
                                // className="inline-flex h-8 w-8 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] hover:text-[#334155] disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => handleDeleteTeamMember(member)}
                                disabled={isTeamMemberPermissionDisabled}
                                aria-label={`Delete team member ${fullName || member.email}`}
                                data-qa={`team-settings-team-member-${member.id}-delete`}
                              >
                                {isDeletingTeamMember ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              {canManageTeamMembers && hasPendingTeamMemberPermissionChanges ? (
                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    className="h-10 bg-[#3B97B1] px-5 text-[16px] font-semibold text-white hover:bg-[#2f849b] disabled:cursor-not-allowed disabled:opacity-70"
                    onClick={handleOpenSaveTeamMemberPermissionsConfirm}
                    disabled={isSavingTeamMemberPermissions || deletingTeamMemberId !== null}
                    data-qa="team-settings-save-permissions"
                  >
                    {isSavingTeamMemberPermissions ? 'Saving...' : 'Save Permissions'}
                  </Button>
                </div>
              ) : null}
              <AlertDialog
                open={isSavePermissionsConfirmOpen}
                onOpenChange={(open) => {
                  if (isSavingTeamMemberPermissions) {
                    return;
                  }
                  setIsSavePermissionsConfirmOpen(open);
                }}
              >
                <AlertDialogContent
                  className="w-full max-w-[calc(100%-2rem)] rounded-[8px] border border-[#D8E0E8] bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.22)] duration-0 data-[state=open]:animate-none data-[state=closed]:animate-none sm:max-w-[520px]"
                  overlayClassName="bg-black/70 data-[state=open]:animate-none data-[state=closed]:animate-none"
                  data-qa="team-settings-save-permissions-confirm-dialog"
                >
                  <AlertDialogHeader className="items-start gap-0 text-left">
                    <AlertDialogTitle className="m-0 text-[18px] font-semibold leading-[1.15] text-[#111827]">
                      Update Permissions
                    </AlertDialogTitle>
                    <AlertDialogDescription className="m-0 mt-3 text-[16px] leading-[1.4] text-[#60778F]">
                      This will update your users' permissions. Are you sure you want to continue?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="mt-8 flex-row justify-end gap-2">
                    <AlertDialogCancel
                      className="h-10 min-w-[108px] border-[#D8E0E8] bg-white px-5 text-[14px] font-semibold text-[#111827] hover:bg-[#f8fafc]"
                      data-qa="team-settings-save-permissions-confirm-cancel"
                    >
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      className="h-10 min-w-[108px] bg-[#3B97B1] px-5 text-[14px] font-semibold text-white hover:bg-[#2f849b]"
                      onClick={handleConfirmSaveTeamMemberPermissions}
                      disabled={isSavingTeamMemberPermissions}
                      data-qa="team-settings-save-permissions-confirm-confirm"
                    >
                      {isSavingTeamMemberPermissions ? 'Saving...' : 'Confirm'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div className="mt-5 w-full max-w-[1200px] rounded-[8px] bg-[#f1f5f980] px-4 py-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <ClipboardList className="h-5 w-5 text-[#64748B]" />
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">
                    Assignees without login
                  </h3>
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#E3EAF1] px-2 text-[12px] font-semibold text-[#334155]">
                    {assigneesWithoutLogin.length}
                  </span>
                </div>
                {canManageTeamMembers ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[14px] font-medium text-[#2F8FB0] hover:text-[#267990]"
                    onClick={() => handleOpenInvitePanel('assignee')}
                    data-qa="team-settings-assignees-add-assignee"
                  >
                    <Plus className="h-4 w-4" />
                    Add assignee
                  </button>
                ) : null}
              </div>

              <p className="mb-0 mt-4 text-[14px] text-[#60778F]">
                People you assign work to but who don't need Quipa access (e.g. installers,
                fabricators, contractors).
              </p>

              {assigneesWithoutLogin.length === 0 ? (
                <div className="mt-5 rounded-[8px] border border-dashed border-[#D8E0E8] px-6 py-8 text-center">
                  <p className="m-0 text-[16px] font-semibold text-[#5D7391]">
                    No assignees without login yet
                  </p>
                  <p className="m-0 mt-4 text-[16px] text-[#60778F]">
                    Add assignees to plan work for contractors or collaborators you manage outside
                    of QUIPA. They won't receive notifications or @mentions.
                  </p>
                </div>
              ) : (
                <div className="mt-5 w-full overflow-x-auto rounded-[8px] border border-[#D8E0E8]">
                  <div className={assigneesTableMinWidth}>
                    <div
                      className={`grid ${assigneesTableColumns} items-end border-b border-[#D8E0E8] px-4 py-4 text-[14px] text-[#546B8B]`}
                    >
                      <div className="font-normal">Name</div>
                      <div className="font-normal">Label</div>
                      <div className="font-normal">Roles</div>
                      {canManageTeamMembers ? <div className="font-normal">Actions</div> : null}
                    </div>

                    {assigneesWithoutLogin.map((assignee, index) => {
                      const assigneeRoleNames = getSelectedRoleNames(assignee.roles);
                      const assigneeReadonlyRolesKey = `assignee-${assignee.id}`;

                      return (
                        <div
                          key={assignee.id}
                          className={`grid ${assigneesTableColumns} items-center px-4 py-4 ${
                            index < assigneesWithoutLogin.length - 1
                              ? 'border-b border-[#D8E0E8]'
                              : ''
                          }`}
                          data-qa={`team-settings-assignee-row-${assignee.id}`}
                        >
                          <div className="min-w-0">
                            {canManageTeamMembers ? (
                              editingAssigneeNameId === assignee.id ? (
                                <div className="inline-flex items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-2 py-2 text-[14px] font-semibold text-[#0F172A]">
                                  <Input
                                    value={editingAssigneeName}
                                    onChange={(e) => setEditingAssigneeName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveAssigneeNameEdit(assignee.id);
                                      }
                                      if (e.key === 'Escape') {
                                        handleCancelAssigneeNameEdit();
                                      }
                                    }}
                                    className="h-8 min-w-[150px] border-[#9BB6D0] text-[14px] font-semibold"
                                    disabled={isAssigneeMutationLoading}
                                    autoFocus
                                    data-qa={`team-settings-assignee-${assignee.id}-name-input`}
                                  />
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[#2F7A8E] hover:bg-[#D6E8EE] disabled:opacity-60"
                                    onClick={() => handleSaveAssigneeNameEdit(assignee.id)}
                                    disabled={isAssigneeMutationLoading}
                                    aria-label={`Save assignee ${assignee.name}`}
                                    data-qa={`team-settings-assignee-${assignee.id}-name-save`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] disabled:opacity-60"
                                    onClick={handleCancelAssigneeNameEdit}
                                    disabled={isAssigneeMutationLoading}
                                    aria-label={`Cancel edit for assignee ${assignee.name}`}
                                    data-qa={`team-settings-assignee-${assignee.id}-name-cancel`}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="inline-flex max-w-full min-w-0 items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-3 py-2 text-[14px] font-semibold text-[#0F172A] hover:bg-[#F5F8FB] disabled:cursor-not-allowed"
                                  onClick={() => handleStartAssigneeNameEdit(assignee)}
                                  disabled={isAssigneeMutationLoading}
                                  aria-label={`Edit assignee ${assignee.name}`}
                                  data-qa={`team-settings-assignee-${assignee.id}-name-edit`}
                                >
                                  <span className="min-w-0 truncate">{assignee.name}</span>
                                  <Pencil className="h-[12px] w-[12px] shrink-0 text-[#3B97B1]" />
                                </button>
                              )
                            ) : (
                              <span className="block truncate text-[14px] font-semibold text-[#0F172A]">
                                {assignee.name}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            {canManageTeamMembers ? (
                              editingAssigneeLabelId === assignee.id ? (
                                <div className="inline-flex items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-2 py-2 text-[14px] text-[#0F172A]">
                                  <Input
                                    value={editingAssigneeLabel}
                                    onChange={(e) =>
                                      setEditingAssigneeLabel(
                                        normalizeAssigneeLabelInput(e.target.value)
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveAssigneeLabelEdit(assignee.id);
                                      }
                                      if (e.key === 'Escape') {
                                        handleCancelAssigneeLabelEdit();
                                      }
                                    }}
                                    className="h-8 w-[88px] border-[#9BB6D0] text-[14px] font-semibold uppercase"
                                    maxLength={4}
                                    disabled={isAssigneeMutationLoading}
                                    autoFocus
                                    data-qa={`team-settings-assignee-${assignee.id}-label-input`}
                                  />
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[#2F7A8E] hover:bg-[#D6E8EE] disabled:opacity-60"
                                    onClick={() => handleSaveAssigneeLabelEdit(assignee.id)}
                                    disabled={isAssigneeMutationLoading}
                                    aria-label={`Save assignee label for ${assignee.name}`}
                                    data-qa={`team-settings-assignee-${assignee.id}-label-save`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] disabled:opacity-60"
                                    onClick={handleCancelAssigneeLabelEdit}
                                    disabled={isAssigneeMutationLoading}
                                    aria-label={`Cancel assignee label edit for ${assignee.name}`}
                                    data-qa={`team-settings-assignee-${assignee.id}-label-cancel`}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="inline-flex max-w-full min-w-0 items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-3 py-2 text-[14px] text-[#0F172A] hover:bg-[#F5F8FB] disabled:cursor-not-allowed"
                                  onClick={() => handleStartAssigneeLabelEdit(assignee)}
                                  disabled={isAssigneeMutationLoading}
                                  aria-label={`Edit assignee label for ${assignee.name}`}
                                  data-qa={`team-settings-assignee-${assignee.id}-label-edit`}
                                >
                                  <span className="min-w-0 truncate">@{assignee.label}</span>
                                  <Pencil className="h-[12px] w-[12px] shrink-0 text-[#3B97B1]" />
                                </button>
                              )
                            ) : (
                              <span className="block truncate text-[14px] text-[#546B8B]">
                                @{assignee.label}
                              </span>
                            )}
                          </div>
                          <div className="relative min-w-0" data-roles-editor>
                            {canManageTeamMembers ? (
                              <button
                                type="button"
                                className="inline-flex w-full max-w-full flex-wrap items-center gap-2 rounded-[8px] border border-dashed border-transparent px-2 py-1 text-left hover:border-[#C2CDDC] hover:bg-[#f1f5f980] disabled:opacity-60"
                                onClick={(event) =>
                                  handleOpenAssigneeRolesEditor(assignee, event.currentTarget)
                                }
                                disabled={isRolesEditorBusy}
                                data-qa={`team-settings-assignee-${assignee.id}-roles-edit`}
                              >
                                {assigneeRoleNames.length === 0 ? (
                                  <span className="text-[14px] text-[#60778F]">No roles</span>
                                ) : (
                                  <>
                                    {assigneeRoleNames.slice(0, 2).map((roleName, roleIndex) => (
                                      <span
                                        key={`assignee-${assignee.id}-role-pill-${roleName}-${roleIndex}`}
                                        className="inline-flex items-center rounded-full bg-[#F3F7FB] px-2 py-[2px] text-[12px] font-semibold text-[#3F5874]"
                                      >
                                        {roleName}
                                      </span>
                                    ))}
                                    {assigneeRoleNames.length > 2 ? (
                                      <span className="text-[12px] font-semibold text-[#3F5874]">
                                        +{assigneeRoleNames.length - 2} more
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </button>
                            ) : (
                              <div className="inline-flex w-full max-w-full flex-wrap items-center gap-2 px-2 py-1 text-left">
                                {assigneeRoleNames.length === 0 ? (
                                  <span className="text-[14px] text-[#60778F]">No roles</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="inline-flex w-full max-w-full flex-wrap items-center gap-2 border-0 bg-transparent p-0 text-left"
                                    onClick={(event) =>
                                      handleOpenReadonlyRolesViewer(
                                        assigneeReadonlyRolesKey,
                                        assigneeRoleNames,
                                        event.currentTarget
                                      )
                                    }
                                    data-readonly-roles-trigger
                                    data-qa={`team-settings-assignee-${assignee.id}-roles-view`}
                                  >
                                    {assigneeRoleNames.slice(0, 2).map((roleName, roleIndex) => (
                                      <span
                                        key={`readonly-assignee-${assignee.id}-role-pill-${roleName}-${roleIndex}`}
                                        className="inline-flex items-center rounded-full bg-[#F3F7FB] px-2 py-[2px] text-[12px] font-semibold text-[#3F5874]"
                                      >
                                        {roleName}
                                      </span>
                                    ))}
                                    {assigneeRoleNames.length > 2 ? (
                                      <span
                                        className="text-[12px] font-semibold text-[#3F5874]"
                                        data-qa={`team-settings-assignee-${assignee.id}-roles-more`}
                                      >
                                        +{assigneeRoleNames.length - 2} more
                                      </span>
                                    ) : null}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {canManageTeamMembers ? (
                            <div className="flex items-center justify-between gap-3">
                              <button
                                type="button"
                                className="inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[14px] font-semibold text-[#2F8FB0] hover:text-[#267990] disabled:opacity-60"
                                onClick={() => handleOpenConvertAssigneePanel(assignee)}
                                disabled={isAssigneeMutationLoading}
                                data-qa={`team-settings-assignee-${assignee.id}-convert`}
                              >
                                <ArrowRight className="h-4 w-4" />
                                Convert to team member
                              </button>
                              <Button
                                variant={'trash-button'}
                                type="button"
                                // className="inline-flex h-8 w-8 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] hover:text-[#334155]"
                                onClick={() => handleDeleteAssignee(assignee.id)}
                                disabled={isAssigneeMutationLoading}
                                aria-label={`Delete assignee ${assignee.name}`}
                                data-qa={`team-settings-assignee-${assignee.id}-delete`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {canViewPendingInvitations && pendingInvitations.length > 0 ? (
              <div className="mt-6 w-full max-w-[1200px]">
                <div className="flex items-center gap-3">
                  <h3 className="m-0 text-[14px] font-semibold text-[#111827]">
                    Pending Invitations
                  </h3>
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#E3EAF1] px-2 text-[12px] font-semibold text-[#334155]">
                    {pendingInvitations.length}
                  </span>
                </div>

                <div className="mt-4 w-full overflow-x-auto rounded-[8px] border border-[#D8E0E8]">
                  <div className="min-w-[900px]">
                    <div className="grid grid-cols-[minmax(360px,2fr)_minmax(110px,1fr)_minmax(110px,1fr)_minmax(120px,1fr)_170px] items-end border-b border-[#D8E0E8] px-4 py-4 text-[14px] text-[#546B8B]">
                      <div className="font-normal">Email</div>
                      <div className="font-normal">Roles</div>
                      <div className="font-normal">Sent</div>
                      <div className="font-normal">Status</div>
                      <div className="font-normal">Actions</div>
                    </div>

                    {pendingInvitations.map((invitation, index) => (
                      <div
                        key={invitation.id}
                        className={`grid grid-cols-[minmax(360px,2fr)_minmax(110px,1fr)_minmax(110px,1fr)_minmax(120px,1fr)_170px] items-center px-4 py-5 ${
                          index < pendingInvitations.length - 1 ? 'border-b border-[#D8E0E8]' : ''
                        }`}
                        data-qa={`team-settings-invitation-row-${invitation.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-[14px] font-semibold text-[#0F172A]">
                            {invitation.email}
                          </span>
                          {invitation.isAdmin ? (
                            <span className="inline-flex items-center rounded-full border border-[#D8DEE8] bg-[#f1f5f980] px-2 py-[1px] text-[12px] font-semibold text-[#111827]">
                              Admin
                            </span>
                          ) : null}
                        </div>
                        <div className="text-[14px] text-[#60778F]">
                          {getRoleLabel(invitation.roleIds)}
                        </div>
                        <div className="text-[14px] text-[#60778F]">{invitation.sentLabel}</div>
                        <div>
                          <span className="inline-flex items-center rounded-full border border-input px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 leading-normal">
                            {invitation.statusLabel}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {canManageTeamMembers ? (
                            <>
                              <button
                                type="button"
                                className="inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[14px] font-semibold text-[#111827] hover:text-[#267990]"
                                onClick={() => handleResendInvitation(invitation)}
                                disabled={isInvitationActionDisabled()}
                                data-qa={`team-settings-invitation-${invitation.id}-resend`}
                              >
                                <RefreshCw className="h-4 w-4" />
                                {resendingInvitationId === invitation.id
                                  ? 'Resending...'
                                  : 'Resend'}
                              </button>
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded text-[#FF3B30] hover:bg-[#FDECEC]"
                                onClick={() => handleDeleteInvitation(invitation)}
                                disabled={isInvitationActionDisabled()}
                                aria-label={`Delete invitation for ${invitation.email}`}
                                data-qa={`team-settings-invitation-${invitation.id}-delete`}
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <span className="text-[13px] text-[#60778F]">View only</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="mt-4 w-full max-w-[1200px] rounded-[8px] bg-[#f1f5f980] px-5 py-6">
            {/* <div className="flex w-full items-center gap-2"> */}
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="New role name"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                disabled={isRoleMutationLoading}
                // className="h-12 flex-1 border-[#C8D0D9] bg-white text-[14px] text-[#24364B] placeholder:text-[#64748B] max-w-xs"
                className="flex min-h-[36px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground hover:border-input-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm max-w-xs"
                data-qa="team-settings-role-name-input"
              />
              <Button
                type="button"
                onClick={handleAddNewRole}
                disabled={!canAddRole}
                className={`min-h-[36px] min-w-[120px] shrink-0 px-5 text-[14px] font-medium text-white transition-colors ${
                  canAddRole ? 'bg-[#3B97B1] hover:bg-[#2f849b]' : 'bg-[#98C3D1] hover:bg-[#8DBBCB]'
                }`}
                data-qa="team-settings-add-role-button"
              >
                <span className="text-[20px] leading-none">+</span>
                <span>Add Role</span>
              </Button>
            </div>

            <div className="mt-5 w-full overflow-x-auto rounded-[8px] border border-[#D8E0E8]">
              <div className="min-w-[520px]">
                <div className="grid grid-cols-[minmax(180px,1fr)_88px_88px_48px] items-end border-b border-[#D8E0E8] px-4 py-6 text-[14px] text-[#546B8B]">
                  <div className="row-span-2 text-[14px] font-normal">Role</div>
                  <div className="col-span-2 text-center text-[14px] font-normal">Assignment</div>
                  <div className="row-span-2" />
                  <div className="text-center text-[14px] font-normal">Projects</div>
                  <div className="text-center text-[14px] font-normal">Items</div>
                </div>

                {roles.length === 0 ? (
                  <div className="px-4 py-8 text-[14px] text-[#64748B]">No roles added yet.</div>
                ) : (
                  roles.map((role, index) => (
                    <div
                      key={role.id}
                      className={`grid grid-cols-[minmax(180px,1fr)_88px_88px_48px] items-center px-4 py-5 ${
                        index < roles.length - 1 ? 'border-b border-[#D8E0E8]' : ''
                      }`}
                      data-qa={`team-settings-role-row-${role.id}`}
                    >
                      <div>
                        {editingRoleId === role.id ? (
                          <div className="inline-flex items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-4 py-3 text-[14px] font-normal text-[#0F172A]">
                            <Input
                              value={editingRoleName}
                              onChange={(e) => setEditingRoleName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveRoleName(role.id);
                                }
                                if (e.key === 'Escape') {
                                  handleCancelRoleNameEdit();
                                }
                              }}
                              className="h-8 min-w-[180px] border-[#9BB6D0] text-[14px] font-semibold"
                              disabled={isSavingRoleName}
                              autoFocus
                              data-qa={`team-settings-role-${role.id}-name-input`}
                            />
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded text-[#2F7A8E] hover:bg-[#D6E8EE] disabled:opacity-60"
                              onClick={() => handleSaveRoleName(role.id)}
                              disabled={isSavingRoleName}
                              aria-label={`Save role ${role.name}`}
                              data-qa={`team-settings-role-${role.id}-name-save`}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] disabled:opacity-60"
                              onClick={handleCancelRoleNameEdit}
                              disabled={isSavingRoleName}
                              aria-label={`Cancel edit for role ${role.name}`}
                              data-qa={`team-settings-role-${role.id}-name-cancel`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex max-w-full items-center gap-2 rounded-[8px] border border-dashed border-[#C2CDDC] px-[8px] py-[4px] text-left text-[14px] text-[#0F172A] hover:bg-[#F5F8FB] disabled:cursor-not-allowed"
                            onClick={() => handleStartRoleNameEdit(role)}
                            disabled={isRoleMutationLoading}
                            aria-label={`Edit role ${role.name}`}
                            data-qa={`team-settings-role-${role.id}-name-edit`}
                          >
                            <span className="truncate font-semibold">{role.name}</span>
                            <Pencil className="h-[12px] w-[12px] text-[#3B97B1]" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={role.projects}
                          onCheckedChange={(checked) =>
                            handleToggleRolePermission(role.id, 'projects', checked === true)
                          }
                          data-qa={`team-settings-role-${role.id}-projects-toggle`}
                        />
                      </div>
                      <div className="flex items-center justify-center">
                        <Checkbox
                          checked={role.items}
                          onCheckedChange={(checked) =>
                            handleToggleRolePermission(role.id, 'items', checked === true)
                          }
                          data-qa={`team-settings-role-${role.id}-items-toggle`}
                        />
                      </div>
                      <div className="flex items-center justify-center">
                        <Button
                          variant={'trash-button'}
                          type="button"
                          // className="inline-flex h-8 w-8 items-center justify-center rounded text-[#64748B] hover:bg-[#DEE6EE] hover:text-[#334155]"
                          onClick={() => handleDeleteRole(role.id)}
                          disabled={isRoleMutationLoading}
                          aria-label={`Delete role ${role.name}`}
                          data-qa={`team-settings-role-${role.id}-delete`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-7">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-[#546B8B]">
                <span>Preview assignment columns:</span>
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-1 text-[#2F8FB0] no-underline hover:text-[#267990]"
                  data-qa="team-settings-preview-projects-link"
                >
                  Projects List
                  <ExternalLink className="h-[14px] w-[14px]" />
                </Link>
                <Link
                  to="/overview"
                  className="inline-flex items-center gap-1 text-[#2F8FB0] no-underline hover:text-[#267990]"
                  data-qa="team-settings-preview-items-link"
                >
                  Items List
                  <ExternalLink className="h-[14px] w-[14px]" />
                </Link>
              </div>
              <p className="m-0 text-[14px] text-[#546B8B]">
                Roles marked for assignment will appear as columns on the respective list.
              </p>
            </div>
          </div>
        )}
      </div>

      {canManageTeamMembers && rolesEditorTarget && rolesEditorPosition
        ? createPortal(
            <div
              className="fixed z-[10030] w-[230px] rounded-[8px] border border-[#D8E0E8] bg-white p-3 shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
              style={{ top: `${rolesEditorPosition.top}px`, left: `${rolesEditorPosition.left}px` }}
              data-roles-editor
              data-qa="team-settings-roles-editor-popup"
            >
              <div className="space-y-2 pr-1">
                {roles.map((role) => (
                  <label
                    key={`roles-editor-option-${role.id}`}
                    className="flex items-center gap-2 text-[14px] text-[#111827]"
                    data-qa={`${
                      rolesEditorTarget.type === 'team_member'
                        ? `team-settings-team-member-${rolesEditorTarget.id}`
                        : `team-settings-assignee-${rolesEditorTarget.id}`
                    }-roles-option-label-${role.id}`}
                  >
                    <Checkbox
                      checked={rolesEditorSelectedRoleIds.includes(role.id)}
                      onCheckedChange={(checked) =>
                        handleRolesEditorRoleChange(role.id, checked === true)
                      }
                      disabled={isRolesEditorBusy}
                      data-qa={`${
                        rolesEditorTarget.type === 'team_member'
                          ? `team-settings-team-member-${rolesEditorTarget.id}`
                          : `team-settings-assignee-${rolesEditorTarget.id}`
                      }-roles-option-${role.id}`}
                    />
                    <span>{role.name}</span>
                  </label>
                ))}
              </div>
            </div>,
            document.body
          )
        : null}

      {readonlyRolesViewer
        ? createPortal(
            <div
              className="fixed z-[10030] w-[230px] rounded-[8px] border border-[#D8E0E8] bg-white p-3 shadow-[0_10px_24px_rgba(15,23,42,0.18)]"
              style={{ top: `${readonlyRolesViewer.top}px`, left: `${readonlyRolesViewer.left}px` }}
              data-readonly-roles-viewer
              data-qa="team-settings-roles-viewer-popup"
            >
              <div className="space-y-2 pr-1">
                {readonlyRolesViewer.roleNames.map((roleName, roleIndex) => (
                  <div
                    key={`roles-viewer-option-${readonlyRolesViewer.key}-${roleName}-${roleIndex}`}
                    className="flex items-center gap-2 text-[14px] text-[#111827]"
                  >
                    {roleName}
                  </div>
                ))}
              </div>
            </div>,
            document.body
          )
        : null}

      {openTablePermissionTooltip && tablePermissionTooltipPosition
        ? createPortal(
            <div
              className="fixed z-[10031] rounded-[8px] border border-[#D8E0E8] bg-white p-3 text-[13px] leading-[1.4] text-[#475569] shadow-md"
              style={{
                top: `${tablePermissionTooltipPosition.top}px`,
                left: `${tablePermissionTooltipPosition.left}px`,
                width: `${tablePermissionTooltipPosition.width}px`,
              }}
              data-table-permission-tooltip-content={openTablePermissionTooltip}
              data-qa={
                openTablePermissionTooltip === 'pricing'
                  ? 'team-settings-table-pricing-help-content'
                  : 'team-settings-table-admin-help-content'
              }
            >
              {openTablePermissionTooltip === 'pricing'
                ? 'View and edit item details, construction settings, and My Shop entries.'
                : 'Manage account settings, users and roles, integrations, and workspace configuration.'}
            </div>,
            document.body
          )
        : null}

      {canManageTeamMembers ? (
        <WideSheet
          open={isInvitePanelOpen}
          onCancel={handleCloseInvitePanel}
          onSave={handleSubmitInvitePanel}
          title="Add"
          subTitle={
            invitePanelMode === 'team_member'
              ? 'Invite a team member with login access'
              : 'Add an assignee without login'
          }
          saveLabel={
            invitePanelMode === 'team_member'
              ? isInvitingMember
                ? 'Sending...'
                : 'Send Invitation'
              : 'Add Assignee'
          }
          cancelLabel="Cancel"
          saveDisabled={!canSubmitInvitePanel}
          overlayDataQa="team-settings-invite-panel-overlay-close"
          overlayStyle={{
            backgroundColor: 'rgba(0, 0, 0, 0.78)',
          }}
          closeButtonDataQa="team-settings-invite-panel-close-button"
          cancelButtonDataQa="team-settings-invite-panel-cancel"
          saveButtonDataQa="team-settings-invite-panel-submit"
        >
          <div>
            <div className="grid grid-cols-2 overflow-hidden rounded-[8px] border border-[#D8E0E8]">
              <button
                type="button"
                className={`border-0 py-3 text-[14px] font-semibold ${
                  invitePanelMode === 'team_member'
                    ? 'bg-[#3B97B1] text-white'
                    : 'bg-white text-[#64748B]'
                }`}
                onClick={() => handleSwitchInviteMode('team_member')}
                data-qa="team-settings-invite-panel-mode-team-member"
              >
                Add Team Member
              </button>
              <button
                type="button"
                className={`border-0 py-3 text-[14px] font-semibold ${
                  invitePanelMode === 'assignee'
                    ? 'bg-[#3B97B1] text-white'
                    : 'bg-white text-[#64748B]'
                }`}
                onClick={() => handleSwitchInviteMode('assignee')}
                data-qa="team-settings-invite-panel-mode-assignee"
              >
                Add Assignee
              </button>
            </div>

            <div className="mt-5 rounded-[8px] border border-[#D8E0E8] bg-[#f1f5f980] px-4 py-4">
              <div className="flex items-center gap-3">
                {invitePanelMode === 'team_member' ? (
                  <UserCheckIcon className="h-5 w-5 text-[#64748B]" />
                ) : (
                  <ClipboardList className="h-5 w-5 text-[#64748B]" />
                )}
                <h3 className="m-0 text-[14px] font-semibold text-[#111827]">
                  {invitePanelMode === 'team_member' ? 'Team Member' : 'Assignees without login'}
                </h3>
              </div>
              <ul className="m-0 mt-3 list-outside list-disc pl-14 text-[14px] text-[#60778F]">
                {invitePanelMode === 'team_member' ? (
                  <>
                    <li>Creates a Quipa user with login access</li>
                    <li>Can log in and view assigned work</li>
                    <li>Can collaborate with the team</li>
                    <li>Receives notifications and @mentions</li>
                    <li>Uses a paid seat</li>
                  </>
                ) : (
                  <>
                    <li>People you assign work to who do not have Quipa access</li>
                    <li>Appear in assignee lists</li>
                    <li>Do not receive notifications or @mentions</li>
                  </>
                )}
              </ul>
            </div>

            {invitePanelMode === 'team_member' ? (
              <>
                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">
                    Contact Information
                  </h3>
                  <label className="mt-4 block text-[14px] font-semibold text-[#111827]">
                    Email
                  </label>
                  <div className="relative mt-2">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                    <Input
                      type="email"
                      placeholder="teammate@company.com"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      className="h-11 pl-9"
                      data-qa="team-settings-invite-member-email-input"
                    />
                  </div>
                </div>

                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Roles</h3>
                  <p className="mb-0 mt-4 text-[14px] text-[#60778F]">
                    Assign work roles to describe what type of work this person does.
                  </p>
                  <div className="mt-4 rounded-[8px] border border-[#D8E0E8] bg-white px-3 py-3">
                    {roles.length === 0 ? (
                      <p className="m-0 text-[14px] text-[#64748B]">
                        No roles created yet. Add roles in the Roles tab first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {roles.map((role) => (
                          <label
                            key={`member-role-${role.id}`}
                            className="flex items-center gap-3 text-[14px] font-semibold text-[#111827]"
                            data-qa={`team-settings-invite-member-role-${role.id}-label`}
                          >
                            <Checkbox
                              checked={memberSelectedRoleIds.includes(role.id)}
                              onCheckedChange={(checked) =>
                                toggleMemberRole(role.id, checked === true)
                              }
                              data-qa={`team-settings-invite-member-role-${role.id}-checkbox`}
                            />
                            <span>{role.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Permissions</h3>
                  <div className="mt-4 flex flex-col gap-3">
                    <div className="relative inline-flex w-fit items-center gap-3 text-[14px] text-[#111827]">
                      <Checkbox
                        checked={memberPricingAccess}
                        onCheckedChange={(checked) => setMemberPricingAccess(checked === true)}
                        data-qa="team-settings-invite-member-pricing-access-checkbox"
                      />
                      <span>Pricing access</span>
                      <PermissionHelpTooltip
                        tooltipKey="pricing"
                        enabled={isInvitePanelOpen}
                        open={openPermissionTooltip === 'pricing'}
                        onOpenChange={(nextOpen) =>
                          setOpenPermissionTooltip(nextOpen ? 'pricing' : null)
                        }
                        ariaLabel="Show pricing access help"
                        content="View and edit item details, construction settings, and My Shop entries."
                        width={290}
                        triggerDataQa="team-settings-invite-member-pricing-access-help"
                        contentDataQa="team-settings-invite-member-pricing-access-help-content"
                      />
                    </div>
                    <div className="relative inline-flex w-fit items-center gap-3 text-[14px] text-[#111827]">
                      <Checkbox
                        checked={memberAdminAccess}
                        onCheckedChange={(checked) => setMemberAdminAccess(checked === true)}
                        data-qa="team-settings-invite-member-admin-access-checkbox"
                      />
                      <span>Administration access</span>
                      <PermissionHelpTooltip
                        tooltipKey="admin"
                        enabled={isInvitePanelOpen}
                        open={openPermissionTooltip === 'admin'}
                        onOpenChange={(nextOpen) =>
                          setOpenPermissionTooltip(nextOpen ? 'admin' : null)
                        }
                        ariaLabel="Show administration access help"
                        content="Manage account settings, users and roles, integrations, and workspace configuration."
                        width={320}
                        triggerDataQa="team-settings-invite-member-admin-access-help"
                        contentDataQa="team-settings-invite-member-admin-access-help-content"
                      />
                    </div>
                  </div>
                </div>

                {isSeatLimitReached ? (
                  <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                    <div className="flex items-center gap-2">
                      <TriangleAlert className="h-5 w-5 text-[#F59E0B]" />
                      <h3 className="m-0 text-[16px] font-semibold text-[#C66A00]">
                        Seat Limit Reached
                      </h3>
                    </div>
                    <p className="m-0 mt-4 text-[14px] text-[#60778F]">
                      You have reached the seat limit for your account.{' '}
                      {accountIsOwner && (
                        <>
                          <button
                            disabled={!accountIsOwner}
                            type="button"
                            className="inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                            onClick={() => handleOpenSeatsModal(true)}
                            data-qa="team_settings_add_seats_button_link"
                          >
                            Add more seats
                          </button>{' '}
                          or{' '}
                          <button
                            type="button"
                            className="inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                            onClick={() => {
                              void handleChangePlan();
                            }}
                            data-qa="team-settings-invite-seat-limit-upgrade-plan"
                          >
                            Upgrade your plan
                          </button>
                        </>
                      )}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {accountIsOwner && (
                        <Button
                          variant={'default'}
                          type="button"
                          className="min-h-[36px]"
                          onClick={() => handleOpenSeatsModal(true)}
                          disabled={!accountIsOwner}
                          data-qa="team_settings_add_seats_button_wide_sheet"
                        >
                          Add Seats
                        </Button>
                      )}

                      <Button
                        variant={'outline'}
                        type="button"
                        className="font-medium min-h-[36px]"
                        onClick={() => {
                          void handleRemoveInactiveMembers();
                        }}
                        disabled={isRemovingInactiveMembers || isInvitationActionDisabled()}
                        data-qa="team-settings-invite-seat-limit-remove-inactive-members"
                      >
                        {isRemovingInactiveMembers ? 'Removing...' : 'Remove inactive members'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-[#64748B]" />
                      <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Seat Usage</h3>
                    </div>
                    <p className="mb-0 mt-4 text-[14px] text-[#111827]">
                      Current: {usedSlots} of {availableSlots} seats used
                    </p>
                    <div className="mt-3 h-[8px] rounded-full bg-[#D7E2E9]">
                      <div
                        className="h-full rounded-full bg-[#3B97B1]"
                        style={{ width: `${seatUsagePercent}%` }}
                      />
                    </div>
                    <p className="m-0 mt-4 text-[14px] text-[#60778F]">
                      Sending this invitation will use 1 seat. You will have{' '}
                      {remainingSeatsAfterInvite} seats remaining.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">
                    Assignee Information
                  </h3>
                  <label className="mt-4 block text-[14px] font-semibold text-[#111827]">
                    Name *
                  </label>
                  <div className="relative mt-2">
                    <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                    <Input
                      type="text"
                      placeholder="John Smith"
                      value={assigneeName}
                      onChange={(e) => handleAssigneeNameChange(e.target.value)}
                      className="h-11 pl-9"
                      data-qa="team-settings-invite-assignee-name-input"
                    />
                  </div>

                  <label className="mt-4 block text-[14px] font-semibold text-[#111827]">
                    Label * <span className="font-normal text-[#60778F]">(1-4 chars)</span>
                  </label>
                  <div className="relative mt-2">
                    <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                    <Input
                      type="text"
                      placeholder="JS"
                      value={assigneeLabel}
                      onChange={(e) => handleAssigneeLabelChange(e.target.value)}
                      className="h-11 pl-9"
                      data-qa="team-settings-invite-assignee-label-input"
                    />
                  </div>
                  <p className="m-0 mt-2 text-[14px] text-[#60778F]">Used for @mentions</p>
                </div>

                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Roles *</h3>
                  <p className="mb-0 mt-4 text-[14px] text-[#60778F]">
                    Select at least one role to describe what type of work this person does.
                  </p>
                  <div className="mt-4 rounded-[8px] border border-[#D8E0E8] bg-white px-3 py-3">
                    {roles.length === 0 ? (
                      <p className="m-0 text-[14px] text-[#64748B]">
                        No roles created yet. Add roles in the Roles tab first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {roles.map((role) => (
                          <label
                            key={`assignee-role-${role.id}`}
                            className="flex items-center gap-3 text-[14px] font-semibold text-[#111827]"
                            data-qa={`team-settings-invite-assignee-role-${role.id}-label`}
                          >
                            <Checkbox
                              checked={assigneeSelectedRoleIds.includes(role.id)}
                              onCheckedChange={(checked) =>
                                toggleAssigneeRole(role.id, checked === true)
                              }
                              data-qa={`team-settings-invite-assignee-role-${role.id}-checkbox`}
                            />
                            <span>{role.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </WideSheet>
      ) : null}

      {canManageTeamMembers ? (
        <WideSheet
          open={isConvertPanelOpen}
          onCancel={handleCloseConvertAssigneePanel}
          onSave={handleSubmitConvertAssigneePanel}
          title="Convert to Team Member"
          subTitle={`Converting ${convertAssigneeTarget?.name} to a team member with login access`}
          saveLabel={isConvertingAssignee ? 'Sending...' : 'Send Invitation'}
          cancelLabel="Cancel"
          saveDisabled={!canSubmitConvertPanel}
          overlayDataQa="team-settings-convert-panel-overlay-close"
          overlayStyle={{
            backgroundColor: 'rgba(0, 0, 0, 0.78)',
          }}
          closeButtonDataQa="team-settings-convert-panel-close-button"
          cancelButtonDataQa="team-settings-convert-panel-cancel"
          saveButtonDataQa="team-settings-convert-panel-submit"
        >
          <div>
            <div className="rounded-[8px] border border-[#D8E0E8] bg-[#f1f5f980] px-4 py-4">
              <div className="flex items-start gap-3">
                <UserCheckIcon className="h-5 w-5 shrink-0 text-[#64748B]" />
                <div className="min-w-0">
                  <h3 className="m-0 text-[14px] font-semibold text-[#111827]">
                    Converting {convertAssigneeTarget?.name} to a Team Member
                  </h3>
                  <p className="m-0 mt-3 text-[14px] text-[#111827]">
                    Name: <span className="font-semibold">{convertAssigneeTarget?.name}</span>
                  </p>
                  <p className="m-0 mt-1 text-[14px] text-[#111827]">
                    Label: <span className="font-semibold">@{convertAssigneeTarget?.label}</span>
                  </p>
                  <p className="m-0 mt-3 text-[14px] text-[#60778F]">
                    Enter their email to send an invitation. Once they accept, they'll have full
                    login access.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
              <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Contact Information</h3>
              <label className="mt-4 block text-[14px] font-semibold text-[#111827]">Email</label>
              <div className="relative mt-2">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                <Input
                  type="email"
                  placeholder="teammate@company.com"
                  value={convertMemberEmail}
                  onChange={(e) => setConvertMemberEmail(e.target.value)}
                  className="h-11 pl-9"
                  data-qa="team-settings-convert-member-email-input"
                />
              </div>
            </div>

            <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
              <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Permissions</h3>
              <div className="mt-4 flex flex-col gap-3">
                <div className="relative inline-flex w-fit items-center gap-3 text-[14px] text-[#111827]">
                  <Checkbox
                    checked={convertMemberPricingAccess}
                    onCheckedChange={(checked) => setConvertMemberPricingAccess(checked === true)}
                    data-qa="team-settings-convert-member-pricing-access-checkbox"
                  />
                  <span>Pricing access</span>
                  <PermissionHelpTooltip
                    tooltipKey="pricing"
                    enabled={isConvertPanelOpen}
                    open={openPermissionTooltip === 'pricing'}
                    onOpenChange={(nextOpen) =>
                      setOpenPermissionTooltip(nextOpen ? 'pricing' : null)
                    }
                    ariaLabel="Show pricing access help"
                    content="View and edit item details, construction settings, and My Shop entries."
                    width={290}
                    triggerDataQa="team-settings-convert-member-pricing-access-help"
                    contentDataQa="team-settings-convert-member-pricing-access-help-content"
                  />
                </div>
                <div className="relative inline-flex w-fit items-center gap-3 text-[14px] text-[#111827]">
                  <Checkbox
                    checked={convertMemberAdminAccess}
                    onCheckedChange={(checked) => setConvertMemberAdminAccess(checked === true)}
                    data-qa="team-settings-convert-member-admin-access-checkbox"
                  />
                  <span>Administration access</span>
                  <PermissionHelpTooltip
                    tooltipKey="admin"
                    enabled={isConvertPanelOpen}
                    open={openPermissionTooltip === 'admin'}
                    onOpenChange={(nextOpen) =>
                      setOpenPermissionTooltip(nextOpen ? 'admin' : null)
                    }
                    ariaLabel="Show administration access help"
                    content="Manage account settings, users and roles, integrations, and workspace configuration."
                    width={320}
                    triggerDataQa="team-settings-convert-member-admin-access-help"
                    contentDataQa="team-settings-convert-member-admin-access-help-content"
                  />
                </div>
              </div>
            </div>

            {isSeatLimitReached ? (
              <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-5 w-5 text-[#F59E0B]" />
                  <h3 className="m-0 text-[16px] font-semibold text-[#C66A00]">
                    Seat Limit Reached
                  </h3>
                </div>
                <p className="m-0 mt-4 text-[14px] text-[#60778F]">
                  You have reached the seat limit for your account.{' '}
                  {accountIsOwner && (
                    <>
                      <button
                        disabled={!accountIsOwner}
                        type="button"
                        className="inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                        onClick={() => handleOpenSeatsModal(true)}
                        data-qa="team_settings_add_seats_button_link"
                      >
                        Add more seats
                      </button>{' '}
                      or{' '}
                      <button
                        type="button"
                        className="inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                        onClick={() => {
                          void handleChangePlan();
                        }}
                        data-qa="team-settings-convert-seat-limit-upgrade-plan"
                      >
                        Upgrade your plan
                      </button>
                    </>
                  )}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {accountIsOwner && (
                    <Button
                      variant={'default'}
                      type="button"
                      className="min-h-[36px]"
                      onClick={() => handleOpenSeatsModal(true)}
                      disabled={!accountIsOwner}
                      data-qa="team_settings_add_seats_button_wide_sheet"
                    >
                      Add Seats
                    </Button>
                  )}
                  <Button
                    variant={'outline'}
                    type="button"
                    className="font-medium min-h-[36px]"
                    onClick={() => {
                      void handleRemoveInactiveMembers();
                    }}
                    disabled={isRemovingInactiveMembers || isInvitationActionDisabled()}
                    data-qa="team-settings-convert-seat-limit-remove-inactive-members"
                  >
                    {isRemovingInactiveMembers ? 'Removing...' : 'Remove inactive members'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-[#64748B]" />
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Seat Usage</h3>
                </div>
                <p className="mb-0 mt-4 text-[14px] text-[#111827]">
                  Current: {usedSlots} of {availableSlots} seats used
                </p>
                <div className="mt-3 h-[8px] rounded-full bg-[#D7E2E9]">
                  <div
                    className="h-full rounded-full bg-[#3B97B1]"
                    style={{ width: `${seatUsagePercent}%` }}
                  />
                </div>
                <p className="m-0 mt-4 text-[14px] text-[#60778F]">
                  Sending this invitation will use 1 seat. You will have {remainingSeatsAfterInvite}{' '}
                  seats remaining.
                </p>
              </div>
            )}
          </div>
        </WideSheet>
      ) : null}
    </div>
  );
};

export default TeamSettingsPage;
